import { getCharacterArt, isArtEnabled } from "../assets/CharacterArt.js";
import { THREE } from "../config/ThreeConfig.js";
import { getConfiguredGLTFLoader } from "../utils/gltfLoader.js";
import { getAbilityIconKeyByName, uiIconUrl, uiSpriteUrl, UI_ICON_SIZES } from "../assets/UIIcons.js";
import { getStarFoxReticleScreenPos } from "../utils/reticleAim.js";

const VIBE_JAM_WIDGET_SRC = "https://vibej.am/2026/widget.js";
const VIBE_JAM_HOME_URL = "https://vibej.am/2026/";

function getThreeWebGLContext(canvas, attributes = {}) {
  if (!canvas || typeof WebGL2RenderingContext === "undefined") return null;
  try {
    return canvas.getContext("webgl2", attributes);
  } catch {
    return null;
  }
}

function ensureVibeJamWidgetScript() {
  if (typeof document === "undefined") return false;
  if (document.querySelector(`script[src="${VIBE_JAM_WIDGET_SRC}"]`)) return true;

  const script = document.createElement("script");
  script.async = true;
  script.src = VIBE_JAM_WIDGET_SRC;
  script.dataset.injectedBy = "hangar-intro-cinematic";
  document.head?.appendChild(script);
  return true;
}

/**
 * Star Mercenaries 64: Boardroom Nebula
 * Cyber UIManager - Military-grade HUD with responsive design
 * 
 * Features:
 * - 6-panel responsive military grid layout
 * - Contextual awareness for character stats and mission info
 * - Mobile/tablet/desktop responsive breakpoints
 * - Character-specific information display
 */

export class CyberUIManager {
  constructor(uiLayer, gameState = {}) {
    this.uiLayer = uiLayer;
    this.gameState = gameState;
    
  // Device detection
  this.deviceType = 'desktop';
  this._narrowViewport = false;
    
    // UI Elements cache
    this.elements = {
      targetAnalysis: null,
      navigationGrid: null,
      vehicleStatus: null,
      missionContext: null,
      weaponStatus: null,
      threatDisplay: null,
      characterStatus: null,
      commsFeed: null,
      specialAbilityIndicator: null,
      reticle: null,
    };
    
    // Animation state
    this.animFrame = null;
    this.gridPulsePhase = 0;
    
    // State caches
    this.currentCharacter = null;
    this.responsiveListener = null;

    // Mission message de-dupe to prevent feedback spam from swamping the HUD
    this._missionMsgLastByKey = new Map();
    this._missionMsgMax = 14;
    this._missionMsgElByKey = new Map();

    // Optional 3D portrait preview (GLB) for the contractor panel
    this._portrait3D = {
      url: null,
      renderer: null,
      scene: null,
      camera: null,
      modelRoot: null,
      isActive: false,
    };

    // 2D portrait expression state (best-effort; falls back gracefully if not available)
    this._portraitState = {
      characterId: null,
      expression: "neutral",
      expiresAt: 0,
      priority: 0,
      lastAppliedUrl: null,
    };

    // Reticle screen-space smoothing.
    this._reticleState = { x: null, y: null };

    // Extra bottom padding reserved for HUD overlays (e.g., special ability indicator)
    // so stacked panels don't obscure each other.
    this._specialHudReservePx = 0;
    this._specialBriefingTimer = null;

  }
  
  async init() {
    this.createHUD();
    this.setupResponsiveControls();
    this.setupDynamicElements();
    this.startAnimations();
    
    return Promise.resolve();
  }

  // Per-frame hook used by the main game loop.
  update(_deltaTime) {
    // Expire portrait expression back to neutral without re-rendering the whole panel.
    const st = this._portraitState;
    if (!st?.expiresAt) return;
    const now = Date.now();
    if (now < st.expiresAt) return;

    st.expiresAt = 0;
    st.priority = 0;
    st.expression = "neutral";
    this.applyCharacterPortraitExpression(this.currentCharacter, "neutral", { force: true });
  }

  getPortraitExpressionPriority(expression) {
    switch (String(expression || "neutral").toLowerCase()) {
      case "damaged":
        return 4;
      case "shocked":
        return 3;
      case "angry":
        return 2;
      case "smug":
        return 1;
      case "neutral":
      default:
        return 0;
    }
  }

  applyCharacterPortraitExpression(characterId, expression, { ttlMs = 900, force = false } = {}) {
    const id = String(characterId || "").toLowerCase();
    if (!id) return false;

    const art = getCharacterArt(id);
    const expr = String(expression || "neutral").toLowerCase();
    const url =
      art?.profileVariants?.[expr] ||
      art?.profileUrl ||
      art?.profileVariants?.neutral ||
      null;

    const portraitEl = document.getElementById("character-portrait");
    if (!portraitEl || !url) return false;

    const now = Date.now();
    const incomingPriority = this.getPortraitExpressionPriority(expr);
    const st = this._portraitState;

    const stillActiveForSame =
      st.characterId === id && st.expiresAt && now < st.expiresAt && st.expression === expr;
    if (!force && stillActiveForSame) {
      // Keep extending the same expression lightly (prevents rapid flicker if firing).
      st.expiresAt = Math.max(st.expiresAt, now + Math.max(120, Number(ttlMs) || 0));
      return true;
    }

    const currentActive =
      st.characterId === id && st.expiresAt && now < st.expiresAt && st.priority > 0;
    if (!force && currentActive && incomingPriority < st.priority) {
      // Don’t let low-priority expressions override damaged/shocked while they’re active.
      return false;
    }

    st.characterId = id;
    st.expression = expr;
    st.priority = incomingPriority;
    st.expiresAt = incomingPriority > 0 ? now + Math.max(120, Number(ttlMs) || 0) : 0;

    if (st.lastAppliedUrl !== url || force) {
      portraitEl.src = url;
      st.lastAppliedUrl = url;
    }
    portraitEl.style.display = "";
    return true;
  }

  setCharacterExpression(characterId, expression, opts = {}) {
    return this.applyCharacterPortraitExpression(characterId, expression, opts);
  }
  
  /**
   * Create the main HUD structure
   */
  createHUD() {
    // Clear existing UI
    this.uiLayer.innerHTML = '';
    
    // Main HUD container with military grid layout
    const hudMain = document.createElement('div');
    hudMain.id = 'hud-main';
    hudMain.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      display: grid;
      grid-template-columns: 250px 1fr 250px;
      grid-template-rows: 120px 1fr 120px;
      grid-gap: 8px;
      padding: 8px;
      box-sizing: border-box;
      pointer-events: none;
      z-index: 1000;
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      font-size: 12px;
      color: #00ff00;
    `;
    
	    // Create the 6 panels
	    this.elements.targetAnalysis = this.createPanel('TARGET ANALYSIS', 'top-left');
	    this.elements.targetAnalysis.style.gridColumn = '1';
	    this.elements.targetAnalysis.style.gridRow = '1';
    
    this.elements.navigationGrid = this.createPanel('NAVIGATION', 'top-center');
    this.elements.navigationGrid.style.gridColumn = '2';
    this.elements.navigationGrid.style.gridRow = '1';
    
	    this.elements.vehicleStatus = this.createPanel('VEHICLE STATUS', 'top-right');
	    this.elements.vehicleStatus.style.gridColumn = '3';
	    this.elements.vehicleStatus.style.gridRow = '1';
    
    // Keep mission text off the playfield (center screen is for combat read).
    this.elements.missionContext = this.createPanel('MISSION CONTEXT', 'left');
    this.elements.missionContext.style.gridColumn = '1';
    this.elements.missionContext.style.gridRow = '2';
    this.elements.missionContext.id = 'mission-context';

    // Keep this panel compact and non-destructive: no blur, light background, no pointer capture.
    this.elements.missionContext.style.cssText += `
      backdrop-filter: none;
      background: rgba(0, 20, 40, 0.18);
      border-color: rgba(0, 255, 255, 0.55);
      box-shadow: 0 0 14px rgba(0, 255, 255, 0.08);
      pointer-events: none;
      width: min(320px, calc(100% - 16px));
      max-height: 260px;
      height: fit-content;
      align-self: center;
      justify-self: start;
      overflow: hidden;
    `;
    
    this.elements.weaponStatus = this.createPanel('WEAPON SYSTEMS', 'top-center');
    this.elements.weaponStatus.id = 'weapon-status';
    this.elements.weaponStatus.style.gridColumn = '2';
    this.elements.weaponStatus.style.gridRow = '1';
    this.elements.weaponStatus.style.width = '460px';
    this.elements.weaponStatus.style.maxWidth = 'calc(100% - 24px)';
    this.elements.weaponStatus.style.maxHeight = '72px';
    this.elements.weaponStatus.style.justifySelf = 'center';
    this.elements.weaponStatus.style.alignSelf = 'end';
    this.elements.weaponStatus.style.background = 'rgba(0, 20, 40, 0.24)';
    
    this.elements.threatDisplay = this.createPanel('THREAT ASSESSMENT', 'bottom-right');
    this.elements.threatDisplay.style.gridColumn = '3';
    this.elements.threatDisplay.style.gridRow = '3';
    
    this.elements.characterStatus = this.createPanel('CONTRACTOR', 'bottom-left');
    this.elements.characterStatus.style.gridColumn = '1';
    this.elements.characterStatus.style.gridRow = '3';
    
    // Add content to panels
    this.setupTargetAnalysis();
    this.setupNavigationGrid();
    this.setupVehicleStatus();
    this.setupWeaponStatus();
    this.setupThreatDisplay();
    this.setupCharacterStatus();
    this.setupCommsFeed();
    
    // Append all panels
    hudMain.appendChild(this.elements.targetAnalysis);
    hudMain.appendChild(this.elements.navigationGrid);
    hudMain.appendChild(this.elements.vehicleStatus);
    hudMain.appendChild(this.elements.missionContext);
    hudMain.appendChild(this.elements.weaponStatus);
    hudMain.appendChild(this.elements.threatDisplay);
    hudMain.appendChild(this.elements.characterStatus);
    hudMain.appendChild(this.elements.commsFeed);
    
    this.uiLayer.appendChild(hudMain);

    // Targeting reticle (always-on in gameplay; hidden when HUD is hidden).
    const reticleStyleId = "sm64-reticle-style";
    if (!document.getElementById(reticleStyleId)) {
      const style = document.createElement("style");
      style.id = reticleStyleId;
      style.textContent = `
        #hud-reticle {
          --reticle-color: rgba(0, 255, 255, 0.92);
          --reticle-glow: 0.25;
          --reticle-size: 42px;
          --reticle-lock: 0;
          --reticle-lock-color: rgba(255, 255, 90, 0.95);
          --reticle-scale: 1;
          --reticle-roll: 0rad;
          position: fixed;
          left: 0;
          top: 0;
          width: var(--reticle-size);
          height: var(--reticle-size);
          transform: translate3d(0, 0, 0) translate(-50%, -50%);
          will-change: transform;
          pointer-events: none;
          z-index: 2147483000;
          opacity: 1;
          mix-blend-mode: normal;
          isolation: isolate;
          contain: layout style paint;
          filter:
            drop-shadow(0 0 2px rgba(0, 0, 0, 1))
            drop-shadow(0 0 7px rgba(0, 255, 255, 0.58));
        }
        #hud-reticle-inner {
          width: 100%;
          height: 100%;
          transform: rotate(var(--reticle-roll)) scale(var(--reticle-scale));
          will-change: transform;
        }
        #hud-reticle::before {
          content: "";
          position: absolute;
          inset: 13px;
          border-radius: 50%;
          border: 2px solid var(--reticle-color);
          background: rgba(0, 0, 0, 0.08);
          box-shadow:
            0 0 0 2px rgba(0, 0, 0, 0.92),
            0 0 14px rgba(0, 255, 255, var(--reticle-glow));
          opacity: 0.96;
        }
        #hud-reticle::after {
          content: "";
          position: absolute;
          left: 50%;
          top: 50%;
          width: 5px;
          height: 5px;
          transform: translate(-50%, -50%);
          border-radius: 50%;
          border: 2px solid rgba(0, 0, 0, 0.95);
          background: var(--reticle-color);
          box-shadow:
            0 0 0 1px rgba(255, 255, 255, 0.76),
            0 0 10px rgba(0, 255, 255, calc(var(--reticle-glow) + 0.18));
          opacity: 1;
        }
        #hud-reticle .r-tick {
          position: absolute;
          background: var(--reticle-color);
          opacity: 1;
          box-shadow:
            0 0 0 1px rgba(0, 0, 0, 0.95),
            0 0 10px rgba(0, 255, 255, var(--reticle-glow));
        }
        #hud-reticle .t-up, #hud-reticle .t-down {
          width: 2px;
          height: 10px;
          left: 50%;
          transform: translateX(-50%);
        }
        #hud-reticle .t-up { top: 6px; }
        #hud-reticle .t-down { bottom: 6px; }
        #hud-reticle .t-left, #hud-reticle .t-right {
          width: 10px;
          height: 2px;
          top: 50%;
          transform: translateY(-50%);
        }
        #hud-reticle .t-left { left: 6px; }
        #hud-reticle .t-right { right: 6px; }

        /* Lock brackets (fade in with --reticle-lock) */
        #hud-reticle .r-lock {
          position: absolute;
          inset: 2px;
          opacity: var(--reticle-lock);
        }
        #hud-reticle .r-lock .c {
          position: absolute;
          width: 9px;
          height: 9px;
          border: 2px solid rgba(255, 255, 90, calc(0.15 + var(--reticle-lock) * 0.75));
          box-shadow:
            0 0 0 1px rgba(0, 0, 0, 0.95),
            0 0 12px rgba(255, 255, 90, calc(0.18 + var(--reticle-lock) * 0.55));
          border-left: none;
          border-top: none;
          border-radius: 2px;
        }
        #hud-reticle .r-lock .tl { left: 0; top: 0; transform: rotate(180deg); }
        #hud-reticle .r-lock .tr { right: 0; top: 0; transform: rotate(270deg); }
        #hud-reticle .r-lock .bl { left: 0; bottom: 0; transform: rotate(90deg); }
        #hud-reticle .r-lock .br { right: 0; bottom: 0; transform: rotate(0deg); }
      `;
      document.head.appendChild(style);
    }

    const reticle = document.createElement("div");
    reticle.id = "hud-reticle";
    reticle.innerHTML = `
      <div id="hud-reticle-inner">
        <div class="r-tick t-up"></div>
        <div class="r-tick t-down"></div>
        <div class="r-tick t-left"></div>
        <div class="r-tick t-right"></div>
        <div class="r-lock">
          <div class="c tl"></div>
          <div class="c tr"></div>
          <div class="c bl"></div>
          <div class="c br"></div>
        </div>
      </div>
    `;
    this.uiLayer.appendChild(reticle);
    this.elements.reticle = reticle;
    
    // Store reference
    this.hudMain = hudMain;
    // Preserve the intended display mode so temporary hides (cinematics, overlays)
    // can restore the HUD without collapsing the grid layout.
    this.hudMain.dataset.smDefaultDisplay = "grid";

    // Overlay: special-ability HUD (created once, updated by Game.animate throttled calls).
    this.ensureSpecialAbilityIndicator();
  }

  ensureSpecialAbilityIndicator() {
    if (this.elements.specialAbilityIndicator) return this.elements.specialAbilityIndicator;

    // Inject keyframes once.
    const styleId = "sm64-special-indicator-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        @keyframes sm64SpecialReadyPulse {
          0% { box-shadow: 0 0 10px rgba(0,255,255,0.12), inset 0 0 10px rgba(0,255,255,0.08); }
          50% { box-shadow: 0 0 22px rgba(0,255,255,0.42), inset 0 0 14px rgba(0,255,255,0.18); }
          100% { box-shadow: 0 0 10px rgba(0,255,255,0.12), inset 0 0 10px rgba(0,255,255,0.08); }
        }
      `;
      document.head.appendChild(style);
    }

    const root = document.createElement("div");
    root.id = "special-ability-indicator";
    root.style.cssText = `
      position: fixed;
      /* Dock under the CONTRACTOR panel (like a compact callout, similar to controls box). */
      left: 8px;
      top: 0px;
      transform: none;
      width: min(320px, calc(100vw - 16px));
      padding: 10px 12px;
      background: rgba(0, 0, 0, 0.72);
      border: 1px solid rgba(0, 255, 255, 0.65);
      border-radius: 6px;
      color: rgba(0, 255, 255, 0.85);
      font-family: 'JetBrains Mono', 'Courier New', monospace;
      pointer-events: none;
      z-index: 10002;
      display: none;
      backdrop-filter: blur(5px);
    `;

    root.innerHTML = `
      <div id="special-ability-top" style="display:flex;align-items:baseline;justify-content:space-between;gap:12px;">
        <div style="display:flex;align-items:baseline;gap:10px;min-width:0;">
          <div style="font-size:10px;letter-spacing:2px;color:rgba(255,200,160,0.8);">SPECIAL</div>
          <div id="special-ability-name" style="font-size:13px;font-weight:800;letter-spacing:1px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">—</div>
        </div>
        <div id="special-ability-status" style="font-size:11px;font-weight:800;letter-spacing:2px;color:rgba(0,255,0,0.75);">—</div>
      </div>
      <div id="special-ability-sub" style="margin-top:6px;display:flex;align-items:center;justify-content:space-between;gap:12px;">
        <div style="display:flex;align-items:center;gap:10px;min-width:0;">
          <div id="special-ability-icon" style="width:28px;height:28px;border-radius:6px;border:1px solid rgba(0,255,255,0.35);background:rgba(0,0,0,0.35);background-size:cover;background-position:center;flex:0 0 auto;"></div>
          <div id="special-ability-hint" style="font-size:11px;color:rgba(0,255,255,0.75);white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">Press E</div>
        </div>
        <div id="special-ability-timer" style="font-size:11px;color:rgba(0,255,255,0.55);">—</div>
      </div>
      <div style="margin-top:8px;height:6px;background:rgba(255,255,255,0.06);border:1px solid rgba(0,255,255,0.25);border-radius:3px;overflow:hidden;">
        <div id="special-ability-bar" style="height:100%;width:0%;background:linear-gradient(90deg, rgba(0,255,255,0.95), rgba(0,255,0,0.85));transition:width 0.12s linear;"></div>
      </div>
    `;

    // Attach to the root UI layer (outside the grid so it never breaks the HUD layout).
    this.uiLayer.appendChild(root);
    this.elements.specialAbilityIndicator = root;
    return root;
  }

  formatMs(ms) {
    const v = Math.max(0, Number(ms) || 0);
    if (v <= 0) return "0.0s";
    const s = v / 1000;
    if (s >= 10) return `${Math.ceil(s)}s`;
    return `${Math.ceil(s * 10) / 10}s`;
  }
  
  /**
   * Create a panel container
   */
  createPanel(title, _position) {
    const panel = document.createElement('div');
    panel.className = 'hud-panel';
    panel.style.cssText = `
      background: rgba(0, 20, 40, 0.24);
      border: 1px solid #00ffff;
      border-radius: 4px;
      padding: 10px;
      pointer-events: auto;
      overflow: hidden;
      position: relative;
      box-shadow: 0 0 14px rgba(0, 255, 255, 0.08),
                  inset 0 0 12px rgba(0, 255, 255, 0.04);
    `;
    
    // Corner decorations
    const corners = ['top-left', 'top-right', 'bottom-left', 'bottom-right'];
    corners.forEach(corner => {
      const deco = document.createElement('div');
      deco.className = `corner ${corner}`;
      deco.style.cssText = `
        position: absolute;
        width: 10px;
        height: 10px;
        border: 2px solid #00ffff;
      `;
      
      switch(corner) {
        case 'top-left':
          deco.style.top = '0';
          deco.style.left = '0';
          deco.style.borderRight = 'none';
          deco.style.borderBottom = 'none';
          break;
        case 'top-right':
          deco.style.top = '0';
          deco.style.right = '0';
          deco.style.borderLeft = 'none';
          deco.style.borderBottom = 'none';
          break;
        case 'bottom-left':
          deco.style.bottom = '0';
          deco.style.left = '0';
          deco.style.borderRight = 'none';
          deco.style.borderTop = 'none';
          break;
        case 'bottom-right':
          deco.style.bottom = '0';
          deco.style.right = '0';
          deco.style.borderLeft = 'none';
          deco.style.borderTop = 'none';
          break;
      }
      panel.appendChild(deco);
    });
    
    // Panel title
    const titleEl = document.createElement('div');
    titleEl.className = 'panel-title';
    titleEl.textContent = `▌ ${title}`;
    titleEl.style.cssText = `
      color: #00ffff;
      font-size: 10px;
      font-weight: bold;
      margin-bottom: 8px;
      letter-spacing: 2px;
      text-transform: uppercase;
    `;
    panel.appendChild(titleEl);
    
    // Content container
    const content = document.createElement('div');
    content.className = 'panel-content';
    content.style.cssText = `
      height: calc(100% - 20px);
      overflow-y: auto;
    `;
    panel.appendChild(content);
    
    return panel;
  }
  
  /**
   * Setup Target Analysis panel (left side)
   */
  setupTargetAnalysis() {
    const content = this.elements.targetAnalysis.querySelector('.panel-content');
    content.innerHTML = `
      <div style="margin-bottom: 10px;">
        <div style="color: #888; font-size: 9px;">PRIMARY TARGET</div>
        <div style="color: #ff6600; font-size: 14px; font-weight: bold;">BOARDROOM NEBULA</div>
      </div>
      <div style="margin-bottom: 10px;">
        <div style="color: #888; font-size: 9px;">THREAT TYPE</div>
        <div style="color: #00ffff;">Corporate Hostile Takeover</div>
      </div>
      <div style="margin-bottom: 10px;">
        <div style="color: #888; font-size: 9px;">CONTRACT VALUE</div>
        <div id="contract-value" style="color: #00ff00; font-size: 16px;">$0</div>
      </div>
      <div>
        <div style="color: #888; font-size: 9px;">ENEMIES NEUTRALIZED</div>
        <div id="enemies-neutralized" style="color: #ff6600;">0</div>
      </div>
    `;
  }
  
  /**
   * Setup Navigation Grid panel (top center)
   */
  setupNavigationGrid() {
    const content = this.elements.navigationGrid.querySelector('.panel-content');
    content.innerHTML = `
      <div style="display: flex; justify-content: space-between; align-items: center; height: 100%;">
        <div style="text-align: center;">
          <div style="color: #888; font-size: 9px;">SECTOR</div>
          <div id="sector-display" style="color: #00ffff; font-size: 18px;">A-1</div>
        </div>
        <div style="text-align: center;">
          <div style="color: #888; font-size: 9px;">COORDINATES</div>
          <div id="coords-display" style="color: #00ff00; font-family: monospace;">000,000</div>
        </div>
        <div style="text-align: center;">
          <div style="color: #888; font-size: 9px;">VELOCITY</div>
          <div id="velocity-display" style="color: #ff6600;">0 m/s</div>
        </div>
      </div>
    `;
  }
  
  /**
   * Setup Vehicle Status panel (right side)
   */
  setupVehicleStatus() {
    const content = this.elements.vehicleStatus.querySelector('.panel-content');
    content.innerHTML = `
      <div style="margin-bottom: 12px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <img alt="" src="${uiIconUrl("hull", UI_ICON_SIZES.small)}" style="width:14px;height:14px;opacity:0.85;image-rendering:pixelated;" />
          <div style="color: #888; font-size: 9px;">HULL INTEGRITY</div>
        </div>
        <div class="bar-container" style="background: #111; height: 8px; border: 1px solid #333; position: relative;">
          <div id="health-bar" class="bar-fill" style="background: linear-gradient(90deg, #ff0000, #ff6600); height: 100%; width: 100%; transition: width 0.3s ease;"></div>
        </div>
        <div id="health-text" style="color: #ff6600; font-size: 10px; margin-top: 2px;">100%</div>
      </div>
      
      <div style="margin-bottom: 12px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <img alt="" src="${uiIconUrl("shield", UI_ICON_SIZES.small)}" style="width:14px;height:14px;opacity:0.85;image-rendering:pixelated;" />
          <div style="color: #888; font-size: 9px;">SHIELD CAPACITORS</div>
        </div>
        <div class="bar-container" style="background: #111; height: 8px; border: 1px solid #333; position: relative;">
          <div id="shield-bar" class="bar-fill" style="background: linear-gradient(90deg, #0066ff, #00ffff); height: 100%; width: 100%; transition: width 0.3s ease;"></div>
        </div>
        <div id="shield-text" style="color: #00ffff; font-size: 10px; margin-top: 2px;">100%</div>
      </div>
      
      <div style="margin-bottom: 12px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:4px;">
          <img alt="" src="${uiIconUrl("boost", UI_ICON_SIZES.small)}" style="width:14px;height:14px;opacity:0.85;image-rendering:pixelated;" />
          <div style="color: #888; font-size: 9px;">BOOST RESERVES</div>
        </div>
        <div class="bar-container" style="background: #111; height: 8px; border: 1px solid #333; position: relative;">
          <div id="boost-bar" class="bar-fill" style="background: linear-gradient(90deg, #6600ff, #ff00ff); height: 100%; width: 100%; transition: width 0.3s ease;"></div>
        </div>
        <div id="boost-text" style="color: #ff00ff; font-size: 10px; margin-top: 2px;">100%</div>
      </div>
      
      <div>
        <div style="color: #888; font-size: 9px; margin-bottom: 4px;">LIVES REMAINING</div>
        <div id="lives-display" style="color: #00ff00; font-size: 20px;">♥♥♥</div>
      </div>
    `;
  }
  
  /**
   * Setup Weapon Systems panel (compact top-center rail)
   */
  setupWeaponStatus() {
    const content = this.elements.weaponStatus.querySelector('.panel-content');
    content.innerHTML = `
      <div style="display: grid; grid-template-columns: minmax(120px, 1fr) auto auto; align-items: center; gap: 14px; height: 100%;">
        <div style="text-align: left; min-width: 0;">
          <div style="color: #888; font-size: 8px; letter-spacing: 1px;">WEAPON</div>
          <div id="weapon-level" style="color: #00ffff; font-size: 13px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis;">TWIN PULSE</div>
        </div>
        <div style="text-align: center;">
          <div style="display:flex;align-items:center;justify-content:center;gap:6px;color:#888;font-size:9px;">
            <img alt="" src="${uiIconUrl("bomb", UI_ICON_SIZES.small)}" style="width:14px;height:14px;opacity:0.85;image-rendering:pixelated;" />
            <span>BOMBS</span>
          </div>
          <div id="bomb-count" style="color: #ff6600; font-size: 16px; line-height: 1;">●●●●●</div>
        </div>
        <div style="text-align: center;">
          <div style="color: #888; font-size: 9px;">EQUITY</div>
          <div id="equity-display" style="color: #00ff00; font-size: 13px;">0%</div>
        </div>
      </div>
    `;
  }
  
  /**
   * Setup Threat Assessment panel (bottom right)
   */
  setupThreatDisplay() {
    const content = this.elements.threatDisplay.querySelector('.panel-content');
    content.innerHTML = `
      <div style="text-align: center;">
        <div style="color: #888; font-size: 9px; margin-bottom: 5px;">THREAT LEVEL</div>
        <div class="threat-level" style="font-size: 24px; font-weight: bold; color: #00ff00; text-shadow: 0 0 10px #00ff00;">LOW</div>
        <div class="threat-bar" style="background: linear-gradient(90deg, #00ff00, #ff0000); height: 4px; width: 30%; margin: 10px auto; border-radius: 2px; transition: width 0.5s ease;"></div>
        <div id="threat-detail" style="color: #888; font-size: 9px;">No immediate danger</div>
      </div>
    `;
    
    this.elements.contextAware = {
      threatIndicator: content.querySelector('.threat-level'),
      threatBar: content.querySelector('.threat-bar')
    };
  }
  
  /**
   * Setup Character Status panel (bottom left)
   */
  setupCharacterStatus() {
    const content = this.elements.characterStatus.querySelector('.panel-content');
    content.innerHTML = `
      <div style="display: flex; flex-direction: column; align-items: center; justify-content: center; height: 100%;">
        <img id="character-portrait" alt="Character portrait" style="display: none; width: 56px; height: 56px; margin-bottom: 6px; border: 1px solid rgba(0, 255, 255, 0.6); border-radius: 4px; image-rendering: pixelated;" />
        <canvas id="character-portrait3d" width="72" height="72" style="display: none; width: 72px; height: 72px; margin-bottom: 6px; border: 1px solid rgba(0, 255, 255, 0.6); border-radius: 4px;"></canvas>
        <div id="character-icon" style="font-size: 32px; margin-bottom: 5px;">🚀</div>
        <div id="character-name" style="color: #00ffff; font-size: 12px; font-weight: bold;">MERCENARY</div>
        <div id="character-quote" style="color: #888; font-size: 8px; text-align: center; margin-top: 5px; font-style: italic;">Ready for contract</div>
      </div>
    `;
  }

  setupCommsFeed() {
    this.elements.commsFeed = this.createPanel('COMMS FEED', 'bottom-right');
    // Dock comms into the right column (middle row) so it never fights the bottom HUD
    // (weapon systems / threat / special ability overlay).
    this.elements.commsFeed.style.gridColumn = '3';
    this.elements.commsFeed.style.gridRow = '2';
    this.elements.commsFeed.style.cssText += `
      width: min(320px, calc(100% - 10px));
      max-height: 200px;
      height: fit-content;
      background: rgba(0, 10, 20, 0.7);
      border-color: rgba(0, 255, 0, 0.4);
      pointer-events: none;
      justify-self: end;
      align-self: start;
    `;
    
    const content = this.elements.commsFeed.querySelector('.panel-content');
    content.style.fontSize = '10px';
    content.style.color = '#00ff00';
    content.innerHTML = '';
  }

  addCommsEntry(characterId, text) {
    const content = this.elements.commsFeed?.querySelector('.panel-content');
    if (!content) return;

    const charNames = {
      honeybadger: 'RAGEBURROW',
      octopus: 'MULTIVEIL',
      elephant: 'GRAY MATTER',
      mantisshrimp: 'CHROMA WAR',
      ant: 'FULFILLMENT',
      narrator: 'NARRATOR',
      admiralbulldog: 'ADMIRAL',
      admiralbloodhound: 'ADMIRAL',
      admiral: 'ADMIRAL',
      logi7: 'LOGI-7',
      civilianpilot: 'CIVILIAN',
      acquisitioncaptain: 'ACQUISITION',
      marketmakeromega: 'MKT-MKR Ω',
      interceptorcaptain: 'CAPTAIN',
      shippingboss: 'LANE NODE',
      volatilityboss: 'VOLATILITY',
      hostileboss: 'WARDEN',
      ledgerboss: 'LEDGER',
      liquidationboss: 'LIQUIDATION',
    };

    const name = charNames[characterId] || 'UNKNOWN';
    const entry = document.createElement('div');
    entry.style.cssText = `
      margin-bottom: 4px;
      border-left: 2px solid #00ff00;
      padding-left: 6px;
      animation: slideIn 0.2s ease-out;
    `;
    entry.innerHTML = `<span style="color: #00ffff; font-weight: bold;">[${name}]</span> ${text}`;
    
    content.appendChild(entry);
    content.scrollTop = content.scrollHeight;

    // Keep only last 5 entries
    while (content.children.length > 5) {
      content.removeChild(content.firstChild);
    }

    // Fade out entry after 6 seconds
    setTimeout(() => {
      entry.style.transition = 'opacity 1s ease';
      entry.style.opacity = '0';
      setTimeout(() => entry.remove(), 1000);
    }, 6000);
  }

  async renderCharacterPortrait3D(glbUrl) {
    const canvas = document.getElementById("character-portrait3d");
    if (!canvas) return false;

    // Three r163+ requires WebGL 2. Requesting a WebGL 1 context makes WebGLRenderer throw.
    const gl = getThreeWebGLContext(canvas, { alpha: true, antialias: true, premultipliedAlpha: false });
    if (!gl) return false;

    if (this._portrait3D.isActive && this._portrait3D.url === glbUrl) return true;
    this._portrait3D.isActive = false;
    this._portrait3D.url = glbUrl;

    if (!this._portrait3D.renderer) {
      this._portrait3D.renderer = new THREE.WebGLRenderer({
        canvas,
        context: gl,
        alpha: true,
        antialias: true,
      });
      this._portrait3D.renderer.setPixelRatio(1);
      this._portrait3D.renderer.outputColorSpace = THREE.SRGBColorSpace;
    }

    const renderer = this._portrait3D.renderer;
    renderer.setSize(canvas.width, canvas.height, false);

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(35, canvas.width / canvas.height, 0.01, 100);

    // Lights tuned for small previews
    scene.add(new THREE.AmbientLight(0xffffff, 1.25));
    const key = new THREE.DirectionalLight(0xffffff, 1.0);
    key.position.set(2, 3, 4);
    scene.add(key);
    const rim = new THREE.DirectionalLight(0x00ffff, 0.65);
    rim.position.set(-3, 1, -2);
    scene.add(rim);

    this._portrait3D.scene = scene;
    this._portrait3D.camera = camera;

    try {
      const loader = await getConfiguredGLTFLoader();
      const gltf = await new Promise((resolve, reject) => {
        loader.load(glbUrl, resolve, undefined, reject);
      });

      const root = gltf?.scene || gltf?.scenes?.[0];
      if (!root) return false;

      // Center + scale to fit
      const box = new THREE.Box3().setFromObject(root);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
      root.position.sub(center);

      const scale = 1.2 / maxDim;
      root.scale.setScalar(scale);

      // Slight angle for “comms portrait” feel
      root.rotation.y = Math.PI * 0.15;
      root.rotation.x = -Math.PI * 0.05;

      root.traverse((obj) => {
        if (!obj?.isMesh) return;
        const material = obj.material;
        if (material && typeof material === "object") {
          material.side = THREE.DoubleSide;
          if ("metalness" in material) material.metalness = 0;
          if ("roughness" in material) material.roughness = 1;
          material.needsUpdate = true;
        }
      });

      scene.add(root);
      this._portrait3D.modelRoot = root;

      camera.position.set(0, 0.35, 2.2);
      camera.lookAt(0, 0.2, 0);

      renderer.render(scene, camera);
      this._portrait3D.isActive = true;
      return true;
    } catch {
      return false;
    }
  }
  
  /**
   * Update character information
   */
  updateCharacter(characterType) {
    this.currentCharacter = characterType;
    
    const icons = {
      honeybadger: '🦡',
      octopus: '🐙',
      elephant: '🐘',
      mantisshrimp: '🦐',
      ant: '🐜'
    };
    
    const names = {
      honeybadger: 'HONEY BADGER',
      octopus: 'OCTOPUS',
      elephant: 'ELEPHANT',
      mantisshrimp: 'MANTIS SHRIMP',
      ant: 'ANT'
    };
    
    const quotes = {
      honeybadger: '"Why are brakes even legal?"',
      octopus: '"Interesting. Continue."',
      elephant: '"Have we modeled second-order effects?"',
      mantisshrimp: '"You are seeing only three dimensions."',
      ant: '"Package delivered."'
    };
    
    const iconEl = document.getElementById('character-icon');
    const portraitEl = document.getElementById('character-portrait');
    const portrait3dEl = document.getElementById('character-portrait3d');
    const nameEl = document.getElementById('character-name');
    const quoteEl = document.getElementById('character-quote');
    
    if (iconEl) iconEl.textContent = icons[characterType] || '🚀';
    if (nameEl) nameEl.textContent = names[characterType] || 'MERCENARY';
    if (quoteEl) quoteEl.textContent = quotes[characterType] || 'Ready for contract';

    // If art exists, prefer portrait over emoji icon.
    const art = getCharacterArt(characterType);
    const resolvedPortraitUrl = art?.profileUrl || art?.profileVariants?.neutral || null;
    if (portraitEl && resolvedPortraitUrl) {
      // Respect any active expression for this character.
      const st = this._portraitState;
      const now = Date.now();
      const hasActiveExpr =
        st?.characterId === String(characterType || "").toLowerCase() &&
        st?.expiresAt &&
        now < st.expiresAt &&
        st.expression &&
        st.expression !== "neutral";
      if (!hasActiveExpr) {
        portraitEl.src = resolvedPortraitUrl;
        if (st) st.lastAppliedUrl = resolvedPortraitUrl;
      } else {
        this.applyCharacterPortraitExpression(characterType, st.expression, { force: true });
      }
      portraitEl.style.display = '';
      if (portrait3dEl) portrait3dEl.style.display = 'none';
      if (iconEl) iconEl.style.display = 'none';
    } else {
      if (portraitEl) portraitEl.style.display = 'none';
      if (portrait3dEl) portrait3dEl.style.display = 'none';
      if (iconEl) iconEl.style.display = '';
    }

    // If we have a character GLB, render a small 3D portrait preview (best effort).
    if (!art?.profileUrl && art?.characterGlbUrl && portrait3dEl) {
      this.renderCharacterPortrait3D(art.characterGlbUrl).then((ok) => {
        if (!ok) return;
        portrait3dEl.style.display = '';
        if (portraitEl) portraitEl.style.display = 'none';
        if (iconEl) iconEl.style.display = 'none';
      });
    }
    
    // Update threat level based on character
    if (characterType === 'honeybadger') {
      this.updateThreatLevel('HIGH');
    } else if (characterType === 'elephant') {
      this.updateThreatLevel('LOW');
    }
  }
  
  /**
   * Update health bar
   */
  updateHealth(health, maxHealth = 100) {
    const percentage = Math.max(0, Math.min(100, (health / maxHealth) * 100));
    const healthBar = document.getElementById('health-bar');
    const healthText = document.getElementById('health-text');

    if (healthBar) healthBar.style.width = `${percentage}%`;
    if (healthText) healthText.textContent = `${Math.round(percentage)}%`;

    if (healthBar) {
      if (percentage < 25) {
        healthBar.style.background = 'linear-gradient(90deg, #ff0000, #ff0000)';
      } else if (percentage < 50) {
        healthBar.style.background = 'linear-gradient(90deg, #ff6600, #ff0000)';
      } else {
        healthBar.style.background = 'linear-gradient(90deg, #ff0000, #ff6600)';
      }
    }

    const isLow = percentage < 30;
    this._setLowHealthWarning(isLow);
  }

  _setLowHealthWarning(on) {
    if (on && !this._lowHealthActive) {
      this._lowHealthActive = true;
      this._showLowHealthOverlay();
      this.game?.soundManager?.playSFX?.('low_health');
    } else if (!on && this._lowHealthActive) {
      this._lowHealthActive = false;
      this._hideLowHealthOverlay();
      this.game?.soundManager?.stopSFX?.('low_health');
    }
  }

  _showLowHealthOverlay() {
    if (this._lowHealthOverlay) return;
    const el = document.createElement('div');
    el.id = 'low-health-overlay';
    el.style.cssText = [
      'position:fixed', 'inset:0', 'pointer-events:none', 'z-index:9999',
      'box-shadow:inset 0 0 80px 20px rgba(255,0,0,0.35)',
      'animation:lowHealthPulse 1s ease-in-out infinite alternate',
    ].join(';');
    document.body.appendChild(el);
    this._lowHealthOverlay = el;
    if (!document.getElementById('low-health-keyframes')) {
      const style = document.createElement('style');
      style.id = 'low-health-keyframes';
      style.textContent = '@keyframes lowHealthPulse{0%{box-shadow:inset 0 0 60px 10px rgba(255,0,0,0.25)}100%{box-shadow:inset 0 0 100px 30px rgba(255,0,0,0.5)}}';
      document.head.appendChild(style);
    }
  }

  _hideLowHealthOverlay() {
    if (this._lowHealthOverlay) {
      this._lowHealthOverlay.remove();
      this._lowHealthOverlay = null;
    }
  }
  
  /**
   * Update shields bar
   */
  updateShields(shields, maxShields = 100) {
    const percentage = Math.max(0, Math.min(100, (shields / maxShields) * 100));
    const shieldBar = document.getElementById('shield-bar');
    const shieldText = document.getElementById('shield-text');
    
    if (shieldBar) shieldBar.style.width = `${percentage}%`;
    if (shieldText) shieldText.textContent = `${Math.round(percentage)}%`;
  }
  
  /**
   * Update boost bar
   */
  updateBoost(boost, maxBoost = 100) {
    const percentage = Math.max(0, Math.min(100, (boost / maxBoost) * 100));
    const boostBar = document.getElementById('boost-bar');
    const boostText = document.getElementById('boost-text');
    
    if (boostBar) boostBar.style.width = `${percentage}%`;
    if (boostText) boostText.textContent = `${Math.round(percentage)}%`;
  }
  
  /**
   * Update lives display
   */
  updateLives(lives) {
    const livesDisplay = document.getElementById('lives-display');
    if (livesDisplay) {
      livesDisplay.textContent = '♥'.repeat(Math.max(0, lives));
    }
  }
  
  /**
   * Update score
   */
  updateScore(score) {
    const contractValue = document.getElementById('contract-value');
    if (contractValue) {
      contractValue.textContent = `$${score.toLocaleString()}`;
    }
    
    // Update enemies neutralized based on score
    const enemiesNeutralized = document.getElementById('enemies-neutralized');
    if (enemiesNeutralized) {
      const estimatedKills = Math.floor(score / 100);
      enemiesNeutralized.textContent = estimatedKills;
    }
  }
  
  /**
   * Update equity
   */
  updateEquity(equity) {
    const equityDisplay = document.getElementById('equity-display');
    if (equityDisplay) {
      equityDisplay.textContent = `${equity}%`;
    }
  }
  
  /**
   * Update bombs display
   */
  updateBombs(bombs) {
    const bombCount = document.getElementById('bomb-count');
    if (bombCount) {
      bombCount.textContent = '●'.repeat(Math.max(0, Math.min(9, bombs)));
    }
  }

  /**
   * Update special ability indicator (cooldown/ready/active).
   * Called from Game.animate on a throttled cadence.
   */
  updateSpecialAbility(state) {
    const root = this.ensureSpecialAbilityIndicator();
    if (!root) return;

    if (!state || !state.name) {
      root.style.display = "none";
      if (this._specialHudReservePx) {
        this._specialHudReservePx = 0;
        this.applyHudBottomPadding();
      }
      return;
    }

    root.style.display = "";

    try {
      const contractor = this.elements?.characterStatus || null;
      const controls = document.getElementById("controls-hint");

      const positionUnderContractor = () => {
        const cRect = contractor?.getBoundingClientRect?.();
        // Default fallback position.
        let left = 8;
        let top = 120;
        let width = 320;

        if (cRect) {
          left = Math.round(cRect.left);
          top = Math.round(cRect.bottom + 8);
          width = Math.round(cRect.width);
        }

        root.style.left = `${left}px`;
        root.style.width = `${Math.max(220, Math.min(width, (window?.innerWidth || width) - left - 8))}px`;
        root.style.right = "auto";
        root.style.bottom = "auto";
        root.style.top = `${top}px`;
        return cRect || null;
      };

      // Pass 1: position it under the contractor, then reserve enough HUD bottom padding
      // so the contractor + special box can stack without obscuring each other.
      let cRect = positionUnderContractor();
      const h = Math.round(root.getBoundingClientRect().height || 0);
      const desiredReserve = h > 0 ? (h + 12) : 0;
      if (Math.abs(desiredReserve - (Number(this._specialHudReservePx) || 0)) > 2) {
        this._specialHudReservePx = desiredReserve;
        this.applyHudBottomPadding();
        // Pass 2 after layout shifts.
        cRect = positionUnderContractor();
      }

      const controlsRect = controls?.getBoundingClientRect?.();
      const rRect = root.getBoundingClientRect();

      // If it still collides with the controls widget (2D overlap), lift it without covering the contractor.
      if (controlsRect) {
        const overlaps =
          rRect.left < controlsRect.right &&
          rRect.right > controlsRect.left &&
          rRect.top < controlsRect.bottom &&
          rRect.bottom > controlsRect.top;

        if (overlaps) {
          const maxBottom = Math.round(controlsRect.top - 8);
          let newTop = Math.round(maxBottom - rRect.height);

          const minTop = cRect ? Math.round(cRect.bottom + 8) : 8;
          if (newTop < minTop && cRect) {
            // Can't fit under the contractor; place it above instead.
            newTop = Math.round(cRect.top - 8 - rRect.height);
          }
          root.style.top = `${Math.max(8, newTop)}px`;
        }
      }
    } catch {
      // ignore
    }

    const nameEl = document.getElementById("special-ability-name");
    const statusEl = document.getElementById("special-ability-status");
    const hintEl = document.getElementById("special-ability-hint");
    const timerEl = document.getElementById("special-ability-timer");
    const barEl = document.getElementById("special-ability-bar");
    const iconEl = document.getElementById("special-ability-icon");

    if (nameEl) nameEl.textContent = String(state.name || "SPECIAL");

    if (iconEl) {
      const key = getAbilityIconKeyByName(state.name);
      if (key) {
        iconEl.style.backgroundImage = `url("${uiIconUrl(key, UI_ICON_SIZES.small)}")`;
      } else {
        iconEl.style.backgroundImage = "";
      }
    }

    const hint = String(state.hint || "E / Y");
    if (hintEl) hintEl.textContent = `Press ${hint}`;

    // Active vs cooldown vs ready visuals.
    const isActive = !!state.isActive;
    const isReady = !!state.isReady;
    const mode = String(state.mode || "");

    root.style.animation = "";
    root.style.borderColor = "rgba(0, 255, 255, 0.65)";

    let barPct = 0;
    let statusText = "COOLDOWN";
    let statusColor = "rgba(0,255,255,0.65)";
    let timerText = "";

    if (isActive) {
      statusText = mode === "primed" ? "PRIMED" : (mode === "charging" ? "CHARGING" : "ACTIVE");
      statusColor = "rgba(255, 200, 160, 0.9)";
      root.style.borderColor = "rgba(255, 200, 160, 0.55)";

      const activeRemainingMs = Math.max(0, Number(state.activeRemainingMs) || 0);
      const specialDurationMs = Math.max(0, Number(state.specialDurationMs) || 0);

      // Prefer explicit progress for charge modes; otherwise show a diminishing active bar.
      if (mode === "charging" || mode === "primed") {
        barPct = Math.max(0, Math.min(1, Number(state.progress) || 0)) * 100;
      } else if (specialDurationMs > 0) {
        barPct = Math.max(0, Math.min(1, activeRemainingMs / specialDurationMs)) * 100;
      } else {
        barPct = Math.max(0, Math.min(1, Number(state.progress) || 0)) * 100;
      }

      timerText = activeRemainingMs > 0 ? this.formatMs(activeRemainingMs) : "";

      // If cooldown exists, show it too (keeps “don’t spam” clear).
      const cooldownRemainingMs = Math.max(0, Number(state.cooldownRemainingMs) || 0);
      if (cooldownRemainingMs > 0 && timerText) timerText = `${timerText} • CD ${this.formatMs(cooldownRemainingMs)}`;
      else if (cooldownRemainingMs > 0) timerText = `CD ${this.formatMs(cooldownRemainingMs)}`;
    } else if (isReady) {
      statusText = "READY";
      statusColor = "rgba(0,255,0,0.95)";
      root.style.animation = "sm64SpecialReadyPulse 0.8s ease-in-out infinite";
      barPct = 100;
      timerText = "—";
    } else {
      statusText = mode ? mode.toUpperCase() : "COOLDOWN";
      statusColor = "rgba(0,255,255,0.65)";
      barPct = Math.max(0, Math.min(1, Number(state.progress) || 0)) * 100;
      const cooldownRemainingMs = Math.max(0, Number(state.cooldownRemainingMs) || 0);
      timerText = cooldownRemainingMs > 0 ? `CD ${this.formatMs(cooldownRemainingMs)}` : "—";
    }

    if (statusEl) {
      statusEl.textContent = statusText;
      statusEl.style.color = statusColor;
      statusEl.style.textShadow = isReady ? "0 0 10px rgba(0,255,0,0.65)" : "none";
    }
    if (timerEl) timerEl.textContent = timerText;
    if (barEl) barEl.style.width = `${Math.max(0, Math.min(100, barPct))}%`;
  }

  showSpecialAbilityBriefing(briefing = {}, options = {}) {
    if (typeof document === "undefined") return null;

    const styleId = "sm64-special-briefing-style";
    if (!document.getElementById(styleId)) {
      const style = document.createElement("style");
      style.id = styleId;
      style.textContent = `
        #special-ability-briefing {
          position: fixed;
          left: 50%;
          top: 16%;
          width: min(560px, calc(100vw - 32px));
          transform: translate(-50%, -10px) scale(0.98);
          z-index: 2147482999;
          pointer-events: none;
          opacity: 0;
          color: #eaffff;
          border: 1px solid rgba(0,255,255,0.78);
          border-left: 4px solid rgba(255, 190, 90, 0.95);
          background:
            linear-gradient(135deg, rgba(4,14,24,0.96), rgba(0,36,46,0.86)),
            repeating-linear-gradient(180deg, rgba(255,255,255,0.055) 0, rgba(255,255,255,0.055) 1px, transparent 1px, transparent 5px);
          box-shadow:
            0 0 0 1px rgba(0,0,0,0.9),
            0 18px 40px rgba(0,0,0,0.5),
            0 0 32px rgba(0,255,255,0.22);
          padding: 16px 18px 15px;
          font-family: 'Courier New', monospace;
          transition: opacity 160ms ease, transform 180ms ease;
        }
        #special-ability-briefing[data-visible="true"] {
          opacity: 1;
          transform: translate(-50%, 0) scale(1);
        }
        #special-ability-briefing .brief-kicker {
          color: rgba(255, 210, 120, 0.95);
          font-size: 11px;
          letter-spacing: 2.4px;
          font-weight: 800;
        }
        #special-ability-briefing .brief-title {
          margin-top: 6px;
          color: #00ffff;
          font-size: 18px;
          letter-spacing: 1.6px;
          font-weight: 900;
          text-shadow: 0 0 14px rgba(0,255,255,0.38);
        }
        #special-ability-briefing .brief-summary {
          margin-top: 9px;
          color: rgba(235,255,255,0.95);
          font-size: 13px;
          line-height: 1.35;
        }
        #special-ability-briefing .brief-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 10px;
          margin-top: 12px;
        }
        #special-ability-briefing .brief-box {
          border: 1px solid rgba(0,255,255,0.24);
          background: rgba(0,0,0,0.22);
          padding: 9px 10px;
        }
        #special-ability-briefing .brief-label {
          color: rgba(0,255,255,0.7);
          font-size: 10px;
          letter-spacing: 1.8px;
          font-weight: 800;
        }
        #special-ability-briefing .brief-copy {
          margin-top: 5px;
          color: rgba(230,255,230,0.96);
          font-size: 12px;
          line-height: 1.35;
        }
        @media (max-width: 720px) {
          #special-ability-briefing { top: 10%; padding: 13px 14px; }
          #special-ability-briefing .brief-grid { grid-template-columns: 1fr; }
        }
      `;
      document.head.appendChild(style);
    }

    let panel = document.getElementById("special-ability-briefing");
    if (!panel) {
      panel = document.createElement("div");
      panel.id = "special-ability-briefing";
      (this.uiLayer || document.body).appendChild(panel);
    }

    const name = String(briefing.name || "SPECIAL ABILITY");
    const hint = String(briefing.hint || "E");
    const summary = String(briefing.summary || "Special ability online.");
    const usage = String(briefing.usage || `Press ${hint} when READY.`);
    const effect = String(briefing.effect || "Applies an immediate combat effect.");
    const tacticalTip = String(briefing.tacticalTip || "Use it deliberately; cooldown is long.");

    panel.replaceChildren();
    const kicker = document.createElement("div");
    kicker.className = "brief-kicker";
    kicker.textContent = "SPECIAL ONLINE";
    const title = document.createElement("div");
    title.className = "brief-title";
    title.textContent = `${name}  //  PRESS ${hint}`;
    const body = document.createElement("div");
    body.className = "brief-summary";
    body.textContent = summary;

    const grid = document.createElement("div");
    grid.className = "brief-grid";
    const makeBox = (label, copy) => {
      const box = document.createElement("div");
      box.className = "brief-box";
      const l = document.createElement("div");
      l.className = "brief-label";
      l.textContent = label;
      const c = document.createElement("div");
      c.className = "brief-copy";
      c.textContent = copy;
      box.append(l, c);
      return box;
    };
    grid.append(makeBox("HOW TO USE", usage), makeBox("GAMEPLAY EFFECT", effect), makeBox("TACTICAL NOTE", tacticalTip));
    panel.append(kicker, title, body, grid);

    if (this._specialBriefingTimer) clearTimeout(this._specialBriefingTimer);
    panel.dataset.visible = "true";
    const durationMs = Math.max(1200, Number(options.durationMs) || 5200);
    this._specialBriefingTimer = setTimeout(() => {
      if (panel?.isConnected) panel.dataset.visible = "false";
    }, durationMs);
    return panel;
  }

  // Screen-space targeting reticle owned by the mouse/trackpad. Shots use the
  // same model, with light target magnetism in Game.getAimDirection().
  updateReticle(player, camera, renderer, deltaTime = 0.016) {
    const el = this.elements.reticle || document.getElementById("hud-reticle");
    if (!el || !player?.object3D || !camera || !renderer) return;

    const game = player?.game;
    const screen = getStarFoxReticleScreenPos(player, camera, renderer, game?._mouseX || 0, game?._mouseY || 0);
    if (!screen) return;

    const st = this._reticleState || (this._reticleState = { x: null, y: null });
    st.x = screen.x;
    st.y = screen.y;
    el.style.transform = `translate3d(${screen.x}px, ${screen.y}px, 0) translate(-50%, -50%)`;

    // Subtle "readable" animation: scale up on boost + tiny pulse with speed, slight rotation with roll.
    const isBoosting = !!player?.isBoosting;
    const vx = Number(player?.velocity?.x) || 0;
    const vy = Number(player?.velocity?.y) || 0;
    const speed = Math.hypot(vx, vy);
    const boostScale = isBoosting ? 0.12 : 0;
    const driftScale = Math.min(0.06, speed / 950);
    const now =
      (globalThis.performance && typeof globalThis.performance.now === "function")
        ? globalThis.performance.now()
        : Date.now();
    const pulse = 0.03 * Math.sin(Number(now) * 0.012);

    const rawRoll = Number(player?.object3D?.rotation?.z) || 0;
    const roll = THREE.MathUtils.clamp(rawRoll * 0.22, -0.65, 0.65);
    const scale = 1 + boostScale + driftScale + pulse;

    // Position is updated every frame from the same reticle model used by shots.
    // Scale/rotation stay on CSS custom properties to avoid fighting the transform.
    el.style.setProperty("--reticle-scale", String(scale));
    el.style.setProperty("--reticle-roll", roll + "rad");

    // Charge/lock-on readability (Star Fox-ish).
    const cs = player?.getChargeState?.() || null;
    const charging = !!cs?.isCharging;
    const lockStrength = Math.max(0, Math.min(1, Number(cs?.lockStrength) || 0));
    const hasLock = !!cs?.hasLock;

    if (charging) {
      const prog = Math.max(0, Math.min(1, Number(cs?.progress) || 0));
      const glow = 0.18 + prog * 0.42 + lockStrength * 0.28;
      el.style.opacity = "1";
      el.style.mixBlendMode = "normal";
      el.style.setProperty("--reticle-color", "rgba(102, 255, 102, 0.95)");
      el.style.setProperty("--reticle-glow", String(Math.max(0, Math.min(0.85, glow))));
      el.style.setProperty("--reticle-lock", String(hasLock ? Math.max(0.4, lockStrength) : (lockStrength * 0.35)));
      el.style.setProperty("--reticle-lock-color", "rgba(255, 255, 90, 0.95)");
    } else {
      el.style.mixBlendMode = "normal";
      el.style.opacity = "1";
      el.style.setProperty("--reticle-color", "rgba(0, 255, 255, 0.92)");
      el.style.setProperty("--reticle-glow", "0.25");
      el.style.setProperty("--reticle-lock", "0");
    }
  }

  getReticleScreenPos(renderer) {
    const st = this._reticleState;
    if (!st || st.x == null || st.y == null) return null;
    const width = renderer?.domElement?.clientWidth || window.innerWidth || 1;
    const height = renderer?.domElement?.clientHeight || window.innerHeight || 1;
    return { x: st.x, y: st.y, width, height };
  }
  
  /**
   * Show pause menu
   */
  showPauseMenu() {
    const pauseOverlay = document.createElement('div');
    pauseOverlay.id = 'pause-overlay';
    pauseOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.8);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 5000;
      font-family: 'JetBrains Mono', monospace;
    `;
    
    pauseOverlay.innerHTML = `
      <div style="max-width: 760px; border: 1px solid #00ffff; background: rgba(0,20,40,0.72); padding: 28px 34px; box-shadow: 0 0 34px rgba(0,255,255,0.22);">
        <div style="color: #00ffff; font-size: 46px; margin-bottom: 10px;">▌ PAUSED</div>
        <div style="color: #ff7a00; font-size: 13px; letter-spacing: 2px; margin-bottom: 22px;">CONTRACT ON HOLD // JUDGE QUICK REFERENCE</div>
        <div style="display:grid; grid-template-columns: 1fr 1fr; gap: 22px; color:#d8ffff; font-size: 13px; line-height: 1.65;">
          <div>
            <div style="color:#00ffff; font-weight:bold; margin-bottom:6px;">ARCADE CONTROLS</div>
            <div><span style="color:#00ff00;">WASD</span> Move corridor target</div>
            <div><span style="color:#00ff00;">Mouse</span> Aim reticle (Pointer Lock opt-in)</div>
            <div><span style="color:#00ff00;">Space</span> Fire / charge shot</div>
            <div><span style="color:#00ff00;">Shift / Ctrl</span> Boost / brake</div>
            <div><span style="color:#00ff00;">E</span> Special ability</div>
          </div>
          <div>
            <div style="color:#00ffff; font-weight:bold; margin-bottom:6px;">WINNING ROUTE</div>
            <div>Protect the civilian freighter.</div>
            <div>Score hits, preserve hull, clear Omega.</div>
            <div>Top-right green portal jumps to the Vibe Jam webring.</div>
            <div>Top-left red portal returns if you arrived via portal.</div>
          </div>
        </div>
        <div style="color: #888; font-size: 14px; margin-top: 24px;">Press P to resume</div>
      </div>
    `;
    
    document.body.appendChild(pauseOverlay);
  }
  
  /**
   * Hide pause menu
   */
  hidePauseMenu() {
    const pauseOverlay = document.getElementById('pause-overlay');
    if (pauseOverlay) {
      pauseOverlay.remove();
    }
  }
  
  /**
   * Show game over screen
   */
  showGameOver(score) {
    const gameOverOverlay = document.createElement('div');
    gameOverOverlay.id = 'gameover-overlay';
    gameOverOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 0, 0, 0.9);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 5000;
      font-family: 'JetBrains Mono', monospace;
    `;
    
    gameOverOverlay.innerHTML = `
      <div style="color: #ff0000; font-size: 48px; margin-bottom: 20px; text-shadow: 0 0 20px #ff0000;">▌ CONTRACT TERMINATED</div>
      <div style="color: #00ffff; font-size: 24px; margin-bottom: 10px;">FINAL SCORE: $${score.toLocaleString()}</div>
      <div style="color: #888; font-size: 14px; margin-top: 20px;">The Boardroom Nebula claims another contractor.</div>
      <div style="color: #444; font-size: 12px; margin-top: 10px;">Refresh to retry</div>
    `;
    
    document.body.appendChild(gameOverOverlay);
  }
  
  /**
   * Show victory screen
   */
  showVictory(score, equity) {
    const victoryOverlay = document.createElement('div');
    victoryOverlay.id = 'victory-overlay';
    victoryOverlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100vw;
      height: 100vh;
      background: rgba(0, 20, 40, 0.95);
      display: flex;
      flex-direction: column;
      justify-content: center;
      align-items: center;
      z-index: 5000;
      font-family: 'JetBrains Mono', monospace;
    `;
    
    victoryOverlay.innerHTML = `
      <div style="color: #00ff00; font-size: 48px; margin-bottom: 20px; text-shadow: 0 0 20px #00ff00;">▌ CONTRACT FULFILLED</div>
      <div style="color: #00ffff; font-size: 24px; margin-bottom: 10px;">TOTAL EARNINGS: $${score.toLocaleString()}</div>
      <div style="color: #ff6600; font-size: 20px; margin-bottom: 10px;">EQUITY ACQUIRED: ${equity}%</div>
      <div style="color: #888; font-size: 14px; margin-top: 20px;">Not heroes. Not villains. Just contractors.</div>
      <div style="color: #444; font-size: 12px; margin-top: 10px;">Refresh to renegotiate</div>
    `;
    
    document.body.appendChild(victoryOverlay);
  }
  
  /**
   * Dynamic Elements: Contextual Awareness
   */
  setupDynamicElements() {
    // Mission messages
    this.createMissionMsg("MISSION INITIATING", 'info');
    this.createMissionMsg("CONTRACT: BOARDROOM NEBULA", 'info');
    this.createMissionMsg("STATUS: ██████░░░░ 40%", 'progress');
    
    // Threat assessment
    this.updateThreatLevel('LOW');
  }
  
  createMissionMsg(message, type = 'info') {
    const contextContainer = document.getElementById('mission-context');
    if (!contextContainer) return;

    // Prevent extreme spam (e.g., collision damage every frame).
    const now = (globalThis.performance && typeof globalThis.performance.now === "function")
      ? globalThis.performance.now()
      : Date.now();
    const dedupeKey = `${type}:${message}`;
    const last = this._missionMsgLastByKey.get(dedupeKey) || 0;
    const existing = this._missionMsgElByKey.get(dedupeKey);
    if (existing && existing.isConnected) {
      if (now - last < 250) return existing;
      this._missionMsgLastByKey.set(dedupeKey, now);
      existing.style.opacity = '1';
      existing.style.transform = 'translateY(0)';
      existing.style.filter = 'drop-shadow(0 0 10px rgba(0,255,255,0.25))';
      setTimeout(() => {
        if (!existing.isConnected) return;
        existing.style.filter = '';
      }, 250);
      contextContainer.scrollTop = contextContainer.scrollHeight;
      return existing;
    }
    if (now - last < 650) return;
    this._missionMsgLastByKey.set(dedupeKey, now);
    
    const msgElement = document.createElement('div');
    msgElement.className = `mission-msg ${type}`;
    msgElement.style.cssText = `
      margin: 2px 0;
      font-size: 0.8rem;
      opacity: 0;
      transform: translateY(10px);
      transition: all 0.3s ease;
    `;
    
    let prefix = '◉';
    switch(type) {
      case 'info':
        msgElement.style.color = '#00ffff';
        prefix = '◉';
        break;
      case 'success':
        msgElement.style.color = '#00ff00';
        prefix = '✅';
        break;
      case 'warning':
        msgElement.style.color = '#ffa500';
        prefix = '⚠️';
        break;
      case 'error':
        msgElement.style.color = '#ff0000';
        prefix = '🚨';
        break;
      case 'progress':
        msgElement.style.color = '#ffffff';
        prefix = '▌';
        break;
    }
    
    msgElement.textContent = `${prefix} ${message}`;
    contextContainer.appendChild(msgElement);
    this._missionMsgElByKey.set(dedupeKey, msgElement);

    // Cap message count to keep DOM light and avoid scroll-jank.
    while (contextContainer.children.length > this._missionMsgMax) {
      contextContainer.removeChild(contextContainer.firstChild);
    }
    if (this._missionMsgElByKey.size > this._missionMsgMax * 4) {
      for (const [k, el] of this._missionMsgElByKey.entries()) {
        if (!el || !el.isConnected) this._missionMsgElByKey.delete(k);
      }
    }
    
    // Animate in
    setTimeout(() => {
      msgElement.style.opacity = '1';
      msgElement.style.transform = 'translateY(0)';
    }, 50);
    
    // Scroll to bottom
    contextContainer.scrollTop = contextContainer.scrollHeight;
    
    return msgElement;
  }
  
  updateThreatLevel(level) {
    const threatIndicator = document.querySelector('.threat-level');
    const threatBar = document.querySelector('.threat-bar');
    const threatDetail = document.getElementById('threat-detail');
    
    if (threatIndicator) {
      threatIndicator.textContent = level;
      
      switch(level) {
        case 'LOW':
          threatIndicator.style.color = '#00ff00';
          threatIndicator.style.textShadow = '0 0 10px #00ff00';
          if (threatBar) threatBar.style.width = '30%';
          if (threatDetail) threatDetail.textContent = 'No immediate danger';
          break;
        case 'MEDIUM':
          threatIndicator.style.color = '#ffa500';
          threatIndicator.style.textShadow = '0 0 10px #ffa500';
          if (threatBar) threatBar.style.width = '60%';
          if (threatDetail) threatDetail.textContent = 'Hostile activity detected';
          break;
        case 'HIGH':
          threatIndicator.style.color = '#ff0000';
          threatIndicator.style.textShadow = '0 0 10px #ff0000';
          if (threatBar) threatBar.style.width = '90%';
          if (threatDetail) threatDetail.textContent = 'Engage with caution';
          break;
        case 'CRITICAL':
          threatIndicator.style.color = '#ff00ff';
          threatIndicator.style.textShadow = '0 0 10px #ff00ff';
          if (threatBar) threatBar.style.width = '100%';
          if (threatDetail) threatDetail.textContent = 'IMMEDIATE THREAT';
          break;
      }
    }
  }
  
  /**
   * Responsive Controls: Mobile/Tablet/Desktop
   */
  setupResponsiveControls() {
    // Detection
    this.detectDeviceType();
    
    // Resize handler
    this.responsiveListener = () => {
      this.detectDeviceType();
      this.applyResponsiveLayout();
    };
    window.addEventListener('resize', this.responsiveListener);
    
    // Initial application
    this.applyResponsiveLayout();
  }
  
  detectDeviceType() {
    const width = Math.max(
      Number(window?.innerWidth) || 0,
      Number(document?.documentElement?.clientWidth) || 0
    );

    this._narrowViewport = width <= 768;

    if (width <= 520) {
      this.deviceType = 'mobile';
    } else if (width <= 900) {
      this.deviceType = 'tablet';
    } else {
      this.deviceType = 'desktop';
    }
  }
  
  applyResponsiveLayout() {
    const hudMain = document.getElementById('hud-main');
    if (!hudMain) return;
    
    switch(this.deviceType) {
    case 'mobile':
      hudMain.style.gridTemplateColumns = '1fr';
      hudMain.style.gridTemplateRows = 'auto auto 1fr auto';
      hudMain.style.gridGap = '2px';
      hudMain.style.padding = '2px 4px';
      if (this.elements.weaponStatus) {
        this.elements.weaponStatus.style.gridColumn = '1';
        this.elements.weaponStatus.style.gridRow = '1';
        this.elements.weaponStatus.style.width = '';
        this.elements.weaponStatus.style.maxWidth = 'calc(100% - 8px)';
        this.elements.weaponStatus.style.maxHeight = '44px';
        this.elements.weaponStatus.style.justifySelf = 'stretch';
        this.elements.weaponStatus.style.alignSelf = 'start';
        this.elements.weaponStatus.style.transform = '';
        this.elements.weaponStatus.style.display = '';
      }
      if (this.elements.vehicleStatus) {
        this.elements.vehicleStatus.style.gridColumn = '1';
        this.elements.vehicleStatus.style.gridRow = '2';
        this.elements.vehicleStatus.style.width = '';
        this.elements.vehicleStatus.style.maxWidth = 'calc(100% - 8px)';
        this.elements.vehicleStatus.style.maxHeight = '44px';
        this.elements.vehicleStatus.style.justifySelf = 'stretch';
        this.elements.vehicleStatus.style.alignSelf = 'start';
        this.elements.vehicleStatus.style.display = '';
      }
      if (this.elements.navigationGrid) {
        this.elements.navigationGrid.style.gridColumn = '1';
        this.elements.navigationGrid.style.gridRow = '3';
        this.elements.navigationGrid.style.width = 'min(320px, calc(100% - 8px))';
        this.elements.navigationGrid.style.justifySelf = 'center';
        this.elements.navigationGrid.style.alignSelf = 'start';
      }
      if (this.elements.targetAnalysis) {
        this.elements.targetAnalysis.style.gridColumn = '1';
        this.elements.targetAnalysis.style.gridRow = '5';
        this.elements.targetAnalysis.style.display = 'none';
      }
      if (this.elements.missionContext) {
        this.elements.missionContext.style.gridColumn = '1';
        this.elements.missionContext.style.gridRow = '5';
        this.elements.missionContext.style.display = 'none';
      }
      if (this.elements.commsFeed) {
        this.elements.commsFeed.style.gridColumn = '1';
        this.elements.commsFeed.style.gridRow = '5';
        this.elements.commsFeed.style.display = 'none';
      }
      if (this.elements.threatDisplay) {
        this.elements.threatDisplay.style.gridColumn = '1';
        this.elements.threatDisplay.style.gridRow = '5';
        this.elements.threatDisplay.style.display = 'none';
      }
      if (this.elements.characterStatus) {
        this.elements.characterStatus.style.gridColumn = '1';
        this.elements.characterStatus.style.gridRow = '4';
      }
      break;

    case 'tablet':
      hudMain.style.gridTemplateColumns = '1fr';
      hudMain.style.gridTemplateRows = 'auto 1fr auto';
      hudMain.style.gridGap = '4px';
      hudMain.style.padding = '4px 8px';

      const tabletPanels = [
        { el: this.elements.targetAnalysis, row: '1' },
        { el: this.elements.vehicleStatus, row: '1' },
        { el: this.elements.weaponStatus, row: '2', width: '', maxWidth: 'calc(100% - 16px)', maxHeight: '52px', justifySelf: 'center', alignSelf: 'end', transform: '' },
        { el: this.elements.missionContext, row: '3' },
        { el: this.elements.characterStatus, row: '3' },
        { el: this.elements.navigationGrid, row: '1', width: 'min(480px, calc(100% - 16px))', justifySelf: 'center', alignSelf: 'start' },
        { el: this.elements.commsFeed, row: '3', justifySelf: 'end', alignSelf: 'start' },
        { el: this.elements.threatDisplay, row: '3' },
      ];
      for (const p of tabletPanels) {
        if (!p.el) continue;
        p.el.style.gridColumn = '1';
        p.el.style.gridRow = p.row;
        if (p.width !== undefined) p.el.style.width = p.width;
        if (p.maxWidth !== undefined) p.el.style.maxWidth = p.maxWidth;
        if (p.maxHeight !== undefined) p.el.style.maxHeight = p.maxHeight;
        if (p.justifySelf !== undefined) p.el.style.justifySelf = p.justifySelf;
        if (p.alignSelf !== undefined) p.el.style.alignSelf = p.alignSelf;
        if (p.transform !== undefined) p.el.style.transform = p.transform;
      }
      break;

    case 'desktop':
      default:
        hudMain.style.gridTemplateColumns = '250px 1fr 250px';
        hudMain.style.gridTemplateRows = '120px 1fr 120px';
        hudMain.style.gridGap = '8px';
        hudMain.style.padding = '8px';
if (this.elements.targetAnalysis) {
    this.elements.targetAnalysis.style.gridColumn = '1';
    this.elements.targetAnalysis.style.gridRow = '1';
    this.elements.targetAnalysis.style.display = '';
  }
  if (this.elements.navigationGrid) {
    this.elements.navigationGrid.style.gridColumn = '2';
    this.elements.navigationGrid.style.gridRow = '1';
    // Keep the top-center HUD compact so it doesn't read like a full-width banner.
    this.elements.navigationGrid.style.width = 'min(640px, calc(100% - 16px))';
    this.elements.navigationGrid.style.justifySelf = 'center';
    this.elements.navigationGrid.style.alignSelf = 'start';
  }
  if (this.elements.vehicleStatus) {
    this.elements.vehicleStatus.style.gridColumn = '3';
    this.elements.vehicleStatus.style.gridRow = '1';
    this.elements.vehicleStatus.style.maxHeight = '';
    this.elements.vehicleStatus.style.justifySelf = '';
    this.elements.vehicleStatus.style.alignSelf = '';
  }
  if (this.elements.missionContext) {
    this.elements.missionContext.style.gridColumn = '1';
    this.elements.missionContext.style.gridRow = '2';
    this.elements.missionContext.style.display = '';
  }
  if (this.elements.commsFeed) {
    this.elements.commsFeed.style.gridColumn = '3';
    this.elements.commsFeed.style.gridRow = '2';
    this.elements.commsFeed.style.justifySelf = 'end';
    this.elements.commsFeed.style.alignSelf = 'start';
    this.elements.commsFeed.style.display = '';
  }
  if (this.elements.weaponStatus) {
    this.elements.weaponStatus.style.gridColumn = '2';
    this.elements.weaponStatus.style.gridRow = '1';
    this.elements.weaponStatus.style.width = '460px';
    this.elements.weaponStatus.style.maxWidth = 'calc(100% - 24px)';
    this.elements.weaponStatus.style.maxHeight = '72px';
    this.elements.weaponStatus.style.justifySelf = 'center';
    this.elements.weaponStatus.style.alignSelf = 'end';
    this.elements.weaponStatus.style.transform = 'translateY(18px)';
    this.elements.weaponStatus.style.display = '';
  }
        if (this.elements.navigationGrid) {
          this.elements.navigationGrid.style.gridColumn = '2';
          this.elements.navigationGrid.style.gridRow = '1';
          // Keep the top-center HUD compact so it doesn’t read like a full-width banner.
          this.elements.navigationGrid.style.width = 'min(640px, calc(100% - 16px))';
          this.elements.navigationGrid.style.justifySelf = 'center';
          this.elements.navigationGrid.style.alignSelf = 'start';
        }
        if (this.elements.vehicleStatus) {
          this.elements.vehicleStatus.style.gridColumn = '3';
          this.elements.vehicleStatus.style.gridRow = '1';
        }
        if (this.elements.missionContext) {
          this.elements.missionContext.style.gridColumn = '1';
          this.elements.missionContext.style.gridRow = '2';
        }
        if (this.elements.commsFeed) {
          this.elements.commsFeed.style.gridColumn = '3';
          this.elements.commsFeed.style.gridRow = '2';
          this.elements.commsFeed.style.justifySelf = 'end';
          this.elements.commsFeed.style.alignSelf = 'start';
        }
        if (this.elements.weaponStatus) {
          this.elements.weaponStatus.style.gridColumn = '2';
          this.elements.weaponStatus.style.gridRow = '1';
          this.elements.weaponStatus.style.width = '460px';
          this.elements.weaponStatus.style.maxWidth = 'calc(100% - 24px)';
          this.elements.weaponStatus.style.maxHeight = '72px';
          this.elements.weaponStatus.style.justifySelf = 'center';
          this.elements.weaponStatus.style.alignSelf = 'end';
          this.elements.weaponStatus.style.transform = 'translateY(18px)';
        }
        if (this.elements.threatDisplay) {
          this.elements.threatDisplay.style.gridColumn = '3';
          this.elements.threatDisplay.style.gridRow = '3';
          this.elements.threatDisplay.style.display = '';
        }
        if (this.elements.characterStatus) {
          this.elements.characterStatus.style.gridColumn = '1';
          this.elements.characterStatus.style.gridRow = '3';
        }
        break;
    }

    this.applyHudBottomPadding();
  }

  applyHudBottomPadding() {
    const hudMain = this.hudMain || document.getElementById("hud-main");
    if (!hudMain) return;

    try {
      const basePad = this.deviceType === "mobile" ? 4 : 8;
      let reserve = 0;

      // Reserve space for the fixed controls overlay so bottom HUD panels never sit behind it.
      const controls = document.getElementById("controls-hint");
      const wh = Number(window?.innerHeight) || 0;
      if (controls && wh) {
        const rect = controls.getBoundingClientRect();
        reserve += Math.round(Math.max(0, wh - rect.top) + 4);
      }

      // Reserve space for fixed overlays that should stack under bottom HUD panels.
      reserve += Math.round(Math.max(0, Number(this._specialHudReservePx) || 0));

      hudMain.style.paddingBottom = `${basePad + reserve}px`;
    } catch {
      // ignore
    }
  }
  
  /**
   * Contextual Warnings
   */
  setupContextualWarnings() {
    // System warnings
    this.createMissionMsg("BOOT SEQUENCE: COMPLETE", 'success');
    this.createMissionMsg("NEURAL INTERFACE: STABLE", 'success');
    this.createMissionMsg("CONTRACT SIGNATURE: VERIFIED", 'info');
    
    // Character load
    const characters = ['honeybadger', 'octopus', 'elephant', 'mantis-shrimp', 'ant'];
    characters.forEach(char => {
      this.createMissionMsg(`LOADING: ${char.toUpperCase()} SYSTEM`, 'progress');
    });
  }
  
  /**
   * Utility: Show game message
   */
  showMessage(message, type = 'info', delay = 3000) {
    const msg = this.createMissionMsg(message, type);
    
    if (delay > 0 && msg) {
      setTimeout(() => {
        msg.style.opacity = '0';
        msg.style.transform = 'translateY(-10px)';
        setTimeout(() => {
          msg.remove();
        }, 300);
      }, delay);
    }
    
    return msg;
  }
  
  /**
   * Start background animations
   */
  startAnimations() {
    const animate = () => {
      this.gridPulsePhase += 0.02;
      
      // Pulse effect on panels
      const panels = document.querySelectorAll('.hud-panel');
      panels.forEach((panel, i) => {
        const offset = i * 0.5;
        const intensity = 0.05 + Math.sin(this.gridPulsePhase + offset) * 0.02;
        panel.style.boxShadow = `
          0 0 ${20 + intensity * 100}px rgba(0, 255, 255, ${intensity}),
          inset 0 0 20px rgba(0, 255, 255, 0.05)
        `;
      });
      
      this.animFrame = requestAnimationFrame(animate);
    };
    
    this.animFrame = requestAnimationFrame(animate);
  }
  
  /**
   * Show the Personality Matrix Briefing Panel
   */
  async showPersonalityMatrix(entity) {
    const {
      name,
      personality,
      quote,
      portraitUrl,
      durationMs = 5000,
      dismissible = true,
    } = entity || {};
    
    const overlay = document.createElement('div');
    overlay.id = 'personality-matrix-overlay';
    overlay.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
  width: 100vw;
  height: 100vh; height: 100dvh;
  background: rgba(0, 5, 15, 0.9);
      display: flex;
      justify-content: center;
      align-items: center;
      z-index: 10000;
      font-family: 'JetBrains Mono', monospace;
      color: #00ffff;
      opacity: 0;
      transition: opacity 0.5s ease;
      backdrop-filter: blur(10px);
    `;

const container = document.createElement('div');
      container.style.cssText = `
width: ${this._narrowViewport ? 'min(600px, calc(100vw - 24px))' : '600px'};
padding: ${this._narrowViewport ? '20px' : '40px'};
      border: 2px solid #00ffff;
      background: rgba(0, 20, 40, 0.8);
      box-shadow: 0 0 50px rgba(0, 255, 255, 0.3), inset 0 0 30px rgba(0, 255, 255, 0.1);
      position: relative;
      overflow: hidden;
    `;

    // Grid background for the panel
    const grid = document.createElement('div');
    grid.style.cssText = `
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      background-image: linear-gradient(rgba(0, 255, 255, 0.05) 1px, transparent 1px), 
                        linear-gradient(90deg, rgba(0, 255, 255, 0.05) 1px, transparent 1px);
      background-size: 20px 20px;
      z-index: -1;
    `;
    container.appendChild(grid);

    // Header
    const header = document.createElement('div');
    header.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: baseline;
      border-bottom: 2px solid #00ffff;
      margin-bottom: 30px;
      padding-bottom: 10px;
    `;
    header.innerHTML = `
      <div style="font-size: 32px; font-weight: bold; letter-spacing: 4px;">▌ ${name}</div>
      <div style="font-size: 12px; color: #888;">CLASSIFIED BRIEFING // LEVEL 4</div>
    `;
    container.appendChild(header);

    // Main Content
    const main = document.createElement('div');
    main.style.cssText = `
      display: grid;
      grid-template-columns: 200px 1fr;
      gap: 40px;
    `;

    // Left: Portrait area
    const portraitArea = document.createElement('div');
    portraitArea.style.cssText = `
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
    `;
    
    if (portraitUrl) {
      const img = document.createElement('img');
      img.src = portraitUrl;
      img.style.cssText = `
        width: 180px;
        height: 180px;
        border: 2px solid #00ffff;
        box-shadow: 0 0 20px rgba(0, 255, 255, 0.5);
        image-rendering: pixelated;
        background: #000;
      `;
      portraitArea.appendChild(img);
    } else {
      const placeholder = document.createElement('div');
      placeholder.style.cssText = `
        width: 180px;
        height: 180px;
        border: 2px dashed #00ffff;
        display: flex;
        justify-content: center;
        align-items: center;
        font-size: 40px;
        color: #00ffff;
      `;
      placeholder.textContent = 'NO DATA';
      portraitArea.appendChild(placeholder);
    }

    const statusTag = document.createElement('div');
    statusTag.style.cssText = `
      margin-top: 20px;
      padding: 4px 12px;
      border: 1px solid #00ffff;
      font-size: 10px;
      background: rgba(0, 255, 255, 0.1);
    `;
    statusTag.textContent = 'STATUS: ACTIVE CONTRACTOR';
    portraitArea.appendChild(statusTag);

    main.appendChild(portraitArea);

    // Right: Personality Matrix
    const matrixArea = document.createElement('div');
    matrixArea.innerHTML = `
      <div style="font-size: 14px; margin-bottom: 20px; color: #888; letter-spacing: 2px;">PERSONALITY MATRIX</div>
    `;

    Object.entries(personality).forEach(([stat, value]) => {
      const row = document.createElement('div');
      row.style.cssText = `
        margin-bottom: 15px;
      `;
      
      const label = document.createElement('div');
      label.style.cssText = `
        display: flex;
        justify-content: space-between;
        font-size: 12px;
        margin-bottom: 4px;
        text-transform: uppercase;
      `;
      label.innerHTML = `<span>${stat}</span><span>${value}%</span>`;
      
      const barContainer = document.createElement('div');
      barContainer.style.cssText = `
        height: 8px;
        background: rgba(0, 255, 255, 0.1);
        border: 1px solid rgba(0, 255, 255, 0.3);
        position: relative;
      `;
      
      const barFill = document.createElement('div');
      barFill.style.cssText = `
        position: absolute;
        left: 0;
        top: 0;
        height: 100%;
        width: 0%;
        background: linear-gradient(90deg, #008888, #00ffff);
        box-shadow: 0 0 10px #00ffff;
        transition: width 1.5s cubic-bezier(0.16, 1, 0.3, 1);
      `;
      
      barContainer.appendChild(barFill);
      row.appendChild(label);
      row.appendChild(barContainer);
      matrixArea.appendChild(row);
      
      // Trigger animation after panel appears
      setTimeout(() => {
        barFill.style.width = `${value}%`;
      }, 100);
    });

    main.appendChild(matrixArea);
    container.appendChild(main);

    // Quote
    const quoteArea = document.createElement('div');
    quoteArea.style.cssText = `
      margin-top: 30px;
      padding: 20px;
      border-top: 1px solid rgba(0, 255, 255, 0.3);
      font-style: italic;
      text-align: center;
      font-size: 16px;
      color: #888;
    `;
    quoteArea.textContent = quote || '"No recorded quotes."';
    container.appendChild(quoteArea);

    overlay.appendChild(container);
    document.body.appendChild(overlay);

    // Animate in
    setTimeout(() => {
      overlay.style.opacity = '1';
    }, 50);

    return new Promise((resolve) => {
      let closed = false;
      const close = () => {
        if (closed) return;
        closed = true;

        window.removeEventListener('keydown', onKeyDown, true);
        overlay.removeEventListener('click', onClick, true);

        overlay.style.opacity = '0';
        setTimeout(() => {
          overlay.remove();
          resolve();
        }, 500);
      };

      const onClick = () => {
        if (!dismissible) return;
        close();
      };

      const onKeyDown = (e) => {
        if (!dismissible) return;
        if (e.code === 'Enter' || e.code === 'Space' || e.code === 'Escape') {
          e.preventDefault();
          close();
        }
      };

      if (dismissible) {
        overlay.addEventListener('click', onClick, true);
        window.addEventListener('keydown', onKeyDown, true);
      }

      // Auto-hide after duration
      const ms = Math.max(0, Number(durationMs) || 0);
      setTimeout(close, ms);
    });
  }

  async showHangarIntroCinematic(options = {}) {
    this.detectDeviceType?.();
    const { steps = [], title = "OPENING", skippable = true, autoAdvance = true } = options;
    const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
    if (!list.length) return false;

    const existing = document.getElementById("hangar-intro-cinematic");
    if (existing) existing.remove();

    ensureVibeJamWidgetScript();
    this.setHUDVisible?.(false);

    const overlay = document.createElement("div");
    overlay.id = "hangar-intro-cinematic";
    overlay.style.cssText = `
      position: fixed;
      inset: 0;
      z-index: 13000;
      background: #010208;
      color: #e8fbff;
      font-family: 'JetBrains Mono', monospace;
      overflow: hidden;
      pointer-events: auto;
    `;

    const canvas = document.createElement("canvas");
    canvas.style.cssText = `
      position: absolute;
      inset: 0;
      width: 100%;
      height: 100%;
      display: block;
      background: #010208;
    `;

    const vignette = document.createElement("div");
    vignette.style.cssText = `
      position: absolute;
      inset: 0;
      background:
        radial-gradient(circle at 50% 42%, rgba(0, 0, 0, 0) 38%, rgba(0, 0, 0, 0.42) 100%),
        linear-gradient(180deg, rgba(5, 8, 18, 0.08), rgba(0, 0, 0, 0.42));
      pointer-events: none;
    `;

    const scanlines = document.createElement("div");
    scanlines.style.cssText = `
      position: absolute;
      inset: 0;
      opacity: 0.08;
      background-image: repeating-linear-gradient(180deg, rgba(255,255,255,0.16) 0, rgba(255,255,255,0.16) 1px, transparent 1px, transparent 5px);
      mix-blend-mode: screen;
      pointer-events: none;
    `;

    const loading = document.createElement("div");
    loading.style.cssText = `
      position: absolute;
      left: 24px;
      top: 22px;
      padding: 8px 12px;
      border-left: 3px solid rgba(255, 64, 64, 0.9);
      color: rgba(225, 248, 255, 0.94);
      background: rgba(0, 0, 0, 0.36);
      letter-spacing: 2px;
      font-size: 12px;
      text-transform: uppercase;
    `;
    loading.textContent = "LOADING HANGAR...";

const plate = document.createElement("div");
      const isNarrowHangar = this._narrowViewport;
      plate.style.cssText = `
position: absolute;
left: ${isNarrowHangar ? '8px' : 'clamp(18px, 5vw, 72px)'};
right: ${isNarrowHangar ? '8px' : 'clamp(18px, 5vw, 72px)'};
bottom: ${isNarrowHangar ? '8px' : 'clamp(18px, 5vh, 58px)'};
min-height: ${isNarrowHangar ? '80px' : '142px'};
padding: ${isNarrowHangar ? '10px 12px' : '18px 22px 16px'};
background: linear-gradient(90deg, rgba(1, 8, 16, 0.88), rgba(4, 22, 34, 0.74));
border-top: 2px solid rgba(126, 232, 255, 0.78);
border-bottom: 1px solid rgba(255, 60, 60, 0.42);
box-shadow: 0 0 38px rgba(0, 210, 255, 0.18), inset 0 0 28px rgba(0, 210, 255, 0.07);
display: grid;
grid-template-columns: ${isNarrowHangar ? '1fr' : 'minmax(150px, 260px) 1fr'};
gap: ${isNarrowHangar ? '8px' : '20px'};
align-items: start;
`;

    const speaker = document.createElement("div");
    speaker.style.cssText = `
      color: #9ff6ff;
      font-size: clamp(15px, 2vw, 22px);
      line-height: 1.1;
      letter-spacing: 2px;
      text-transform: uppercase;
      font-weight: 800;
    `;

    const metadata = document.createElement("div");
    metadata.style.cssText = `
      margin-top: 10px;
      color: rgba(255, 178, 112, 0.86);
      font-size: 11px;
      line-height: 1.35;
      letter-spacing: 1px;
      text-transform: uppercase;
    `;

    const speakerWrap = document.createElement("div");
    speakerWrap.appendChild(speaker);
    speakerWrap.appendChild(metadata);

    const dialogue = document.createElement("div");
    dialogue.style.cssText = `
      color: #ecfff2;
      font-size: clamp(17px, 2.2vw, 28px);
      line-height: 1.38;
      white-space: pre-line;
      text-shadow: 0 2px 0 rgba(0,0,0,0.72);
      min-width: 0;
    `;

    const topBar = document.createElement("div");
    topBar.style.cssText = `
      position: absolute;
      left: clamp(18px, 5vw, 72px);
      right: clamp(18px, 5vw, 72px);
      top: clamp(16px, 4vh, 42px);
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 18px;
      color: rgba(196, 246, 255, 0.88);
      font-size: 11px;
      letter-spacing: 2px;
      text-transform: uppercase;
      pointer-events: none;
    `;

    const titleNode = document.createElement("div");
    titleNode.textContent = title;
    const hint = document.createElement("div");
    hint.textContent = skippable ? "ENTER ADVANCE / ESC SKIP" : "ENTER ADVANCE";
    topBar.appendChild(titleNode);
    topBar.appendChild(hint);

    const jamWidget = document.createElement("a");
    jamWidget.id = "vibejam-cinematic-widget";
    jamWidget.href = VIBE_JAM_HOME_URL;
    jamWidget.target = "_blank";
    jamWidget.rel = "noopener noreferrer";
    jamWidget.dataset.widgetSrc = VIBE_JAM_WIDGET_SRC;
    jamWidget.setAttribute("aria-label", "Vibe Jam 2026 widget");
    jamWidget.style.cssText = `
      position: absolute;
      right: clamp(18px, 5vw, 72px);
      top: clamp(50px, 8vh, 86px);
      z-index: 3;
      display: inline-flex;
      align-items: center;
      gap: 8px;
      padding: 7px 10px;
      border: 1px solid rgba(255, 255, 255, 0.34);
      background: rgba(0, 0, 0, 0.44);
      color: rgba(255, 255, 255, 0.94);
      text-decoration: none;
      font-size: 11px;
      line-height: 1;
      letter-spacing: 1.7px;
      text-transform: uppercase;
      box-shadow: 0 0 18px rgba(0, 255, 255, 0.16);
      pointer-events: auto;
    `;
    const jamPulse = document.createElement("span");
    jamPulse.setAttribute("aria-hidden", "true");
    jamPulse.style.cssText = `
      width: 8px;
      height: 8px;
      border-radius: 50%;
      background: #20ff88;
      box-shadow: 0 0 12px rgba(32, 255, 136, 0.9);
      flex: 0 0 auto;
    `;
    const jamLabel = document.createElement("span");
    jamLabel.textContent = "VIBE JAM 2026";
    jamWidget.append(jamPulse, jamLabel);

    plate.appendChild(speakerWrap);
    plate.appendChild(dialogue);
    overlay.appendChild(canvas);
    overlay.appendChild(vignette);
    overlay.appendChild(scanlines);
    overlay.appendChild(topBar);
    overlay.appendChild(jamWidget);
    overlay.appendChild(loading);
    overlay.appendChild(plate);
    document.body.appendChild(overlay);

    const normalizeSpeakerId = (value) =>
      String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9]/g, "");

    const speakerNames = {
      honeybadger: "RAGEBURROW",
      octopus: "MULTIVEIL",
      elephant: "GRAY MATTER",
      mantisshrimp: "CHROMA WAR",
      ant: "FULFILLMENT QUEEN",
      narrator: "ALERT SIREN",
    };

    const stageSlots = {
      honeybadger: { x: -4.8, y: 0, z: 0.1, rotationY: 0.28 },
      octopus: { x: -2.35, y: 0, z: -0.55, rotationY: 0.12 },
      elephant: { x: 0, y: 0, z: -0.85, rotationY: 0 },
      mantisshrimp: { x: 2.35, y: 0, z: -0.55, rotationY: -0.12 },
      ant: { x: 4.8, y: 0, z: 0.1, rotationY: -0.28 },
    };

    const disposeObject = (object3D) => {
      if (!object3D) return;
      object3D.traverse?.((child) => {
        if (child?.geometry?.dispose) child.geometry.dispose();
        const disposeMaterial = (material) => {
          if (!material) return;
          if (Array.isArray(material)) {
            material.forEach(disposeMaterial);
            return;
          }
          for (const key of ["map", "emissiveMap", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "alphaMap"]) {
            const texture = material[key];
            if (texture?.dispose) texture.dispose();
          }
          material.dispose?.();
        };
        disposeMaterial(child?.material);
      });
    };

    let renderer = null;
    let scene = null;
    let camera = null;
    let glContext = null;
    let animationFrame = null;
    let activeSpeakerId = null;
    let currentStepIndex = 0;
    let closed = false;
    let resolver = null;
    let timerId = null;
    let modelLoadPromise = null;
    const loadedModels = new Map();
    const reusableVectors = {
      cameraTarget: new THREE.Vector3(0, 1.1, 0),
      desiredCamera: new THREE.Vector3(0, 2.8, 8.5),
      desiredLook: new THREE.Vector3(0, 1.1, 0),
    };

    const hasWebGL = () => {
      glContext = getThreeWebGLContext(canvas, { alpha: false, antialias: true });
      return !!glContext;
    };

    const updateSize = () => {
      if (!renderer || !camera) return;
      const width = Math.max(1, (window.visualViewport?.width) || (window.innerWidth || 1));
      const height = Math.max(1, (window.visualViewport?.height) || (window.innerHeight || 1));
      renderer.setSize(width, height, false);
      camera.aspect = width / height;
      if (width / height < 1.2) {
        const refAspect = 16 / 9;
        const hFovRad = 2 * Math.atan(Math.tan((39 / 2) * (Math.PI / 180)) * refAspect);
        camera.fov = Math.min(70, Math.max(39, THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(hFovRad / 2) / (width / height)))));
      } else {
        camera.fov = 39;
      }
      camera.updateProjectionMatrix();
    };

    const makeHangar = () => {
      const hangarGroup = new THREE.Group();
      scene.add(hangarGroup);

      const floor = new THREE.Mesh(
        new THREE.PlaneGeometry(16, 18),
        new THREE.MeshStandardMaterial({
          color: 0x151a24,
          roughness: 0.88,
          metalness: 0.18,
          emissive: 0x020608,
        })
      );
      floor.rotation.x = -Math.PI / 2;
      floor.position.z = -1.6;
      hangarGroup.add(floor);

      const runway = new THREE.Mesh(
        new THREE.PlaneGeometry(2.1, 18),
        new THREE.MeshBasicMaterial({ color: 0x0b2730, transparent: true, opacity: 0.62 })
      );
      runway.rotation.x = -Math.PI / 2;
      runway.position.set(0, 0.012, -1.7);
      hangarGroup.add(runway);

      const grid = new THREE.GridHelper(16, 18, 0x1cf0ff, 0x29404a);
      grid.material.transparent = true;
      grid.material.opacity = 0.22;
      grid.position.y = 0.018;
      grid.position.z = -1.6;
      hangarGroup.add(grid);

      const wallMaterial = new THREE.MeshStandardMaterial({
        color: 0x080d16,
        roughness: 0.92,
        metalness: 0.25,
        emissive: 0x03060a,
      });
      const backWall = new THREE.Mesh(new THREE.BoxGeometry(16, 6, 0.22), wallMaterial);
      backWall.position.set(0, 2.9, -7.8);
      hangarGroup.add(backWall);

      for (let index = 0; index < 7; index++) {
        const x = -7.2 + index * 2.4;
        const pillar = new THREE.Mesh(
          new THREE.BoxGeometry(0.12, 4.8, 0.18),
          new THREE.MeshStandardMaterial({ color: 0x162334, roughness: 0.8, metalness: 0.35 })
        );
        pillar.position.set(x, 2.45, -7.58);
        hangarGroup.add(pillar);
      }

      for (let index = 0; index < 8; index++) {
        const warning = new THREE.Mesh(
          new THREE.BoxGeometry(0.75, 0.05, 0.75),
          new THREE.MeshBasicMaterial({ color: index % 2 ? 0xff4338 : 0xffb000 })
        );
        warning.position.set(-6.2 + index * 1.78, 0.026, 5.4);
        hangarGroup.add(warning);
      }
    };

    const frameModel = (root, slot) => {
      const box = new THREE.Box3().setFromObject(root);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const height = Math.max(size.y || 1, 0.001);
      // Anchor full-profile GLBs by their feet instead of their center. Center
      // anchoring put half the model below the stage line, which made the bottom
      // dialogue plate cut characters off around the knees.
      root.position.sub(new THREE.Vector3(center.x, box.min.y, center.z));
      root.scale.setScalar(2.18 / height);
      const framed = new THREE.Group();
      framed.position.set(slot.x, slot.y, slot.z);
      framed.rotation.y = slot.rotationY;
      framed.add(root);
      framed.userData.baseY = slot.y;
      framed.userData.baseRotationY = slot.rotationY;
      return framed;
    };

    const makePlaceholderModel = (speakerId, slot) => {
      const framed = new THREE.Group();
      framed.name = `hangar-placeholder-${speakerId}`;
      framed.position.set(slot.x, slot.y, slot.z);
      framed.rotation.y = slot.rotationY;
      framed.userData.baseY = slot.y;
      framed.userData.baseRotationY = slot.rotationY;
      framed.userData.isPlaceholder = true;

      const colorBySpeaker = {
        honeybadger: 0xff6d3a,
        octopus: 0x7edcff,
        elephant: 0xe7edf0,
        mantisshrimp: 0x61ffca,
        ant: 0xffb84a,
      };
      const color = colorBySpeaker[speakerId] || 0x8af5ff;
      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color,
        emissiveIntensity: 0.14,
        roughness: 0.7,
        metalness: 0.1,
        transparent: true,
        opacity: 0.42,
      });
      const body = new THREE.Mesh(new THREE.CapsuleGeometry(0.42, 1.0, 4, 8), material);
      body.position.y = 0.85;
      const head = new THREE.Mesh(new THREE.SphereGeometry(0.38, 12, 8), material);
      head.position.y = 1.62;
      const base = new THREE.Mesh(
        new THREE.CylinderGeometry(0.62, 0.72, 0.06, 16),
        new THREE.MeshBasicMaterial({ color, transparent: true, opacity: 0.24 })
      );
      base.position.y = 0.03;
      framed.add(body, head, base);
      scene.add(framed);
      loadedModels.set(speakerId, framed);
      return framed;
    };

    const tuneModelMaterials = (root) => {
      root.traverse?.((child) => {
        if (!child?.isMesh) return;
        child.castShadow = false;
        child.receiveShadow = false;
        const materials = Array.isArray(child.material) ? child.material : [child.material];
        for (const material of materials) {
          if (!material) continue;
          material.side = THREE.DoubleSide;
          if ("roughness" in material) material.roughness = Math.min(1, Math.max(0.55, material.roughness ?? 0.8));
          if ("metalness" in material) material.metalness = Math.min(0.35, material.metalness ?? 0.1);
          material.needsUpdate = true;
        }
      });
    };

    const streamHeroModels = async (speakerUrls) => {
      if (!speakerUrls?.size) {
        loading.style.display = "none";
        return;
      }

      let loadedCount = 0;
      try {
        const loader = await getConfiguredGLTFLoader();
        const loadOne = async ([speakerId, modelUrl]) => {
          if (closed) return;
          try {
            const gltf = await new Promise((resolve, reject) => {
              loader.load(modelUrl, resolve, undefined, reject);
            });
            const root = gltf?.scene || gltf?.scenes?.[0];
            if (!root) return;
            if (closed) {
              disposeObject(root);
              return;
            }
            tuneModelMaterials(root);
            const framed = frameModel(root, stageSlots[speakerId]);
            const previous = loadedModels.get(speakerId);
            if (previous) {
              scene.remove(previous);
              disposeObject(previous);
            }
            scene.add(framed);
            loadedModels.set(speakerId, framed);
            loadedCount += 1;
            overlay.dataset.loadedModels = String(loadedCount);
            loading.textContent = `STREAMING CAST ${loadedCount}/${speakerUrls.size}`;
          } catch {
            // Keep the cinematic running even if one profile asset is unavailable.
          }
        };
        await Promise.all(Array.from(speakerUrls.entries()).map(loadOne));
      } finally {
        if (!closed) loading.style.display = "none";
      }
    };

    const initThree = async () => {
      overlay.dataset.stageStatus = "loading";
      if (!hasWebGL()) {
        overlay.dataset.stageStatus = "webgl2-unavailable";
        loading.textContent = "HANGAR MODEL STAGE REQUIRES WEBGL 2";
        return false;
      }
try {
      renderer = new THREE.WebGLRenderer({ canvas, context: glContext, antialias: true, alpha: false });
      const isNarrowHangarInit = this._narrowViewport;
      renderer.setPixelRatio(Math.min(window.devicePixelRatio || 1, isNarrowHangarInit ? 1.0 : 1.25));
      renderer.outputColorSpace = THREE.SRGBColorSpace;
      renderer.setClearColor(0x010208, 1);

      scene = new THREE.Scene();
      scene.fog = new THREE.Fog(0x010208, 8, 19);
      const hangarBaseFOV = 39;
      const hangarAspect = Math.max(0.1, (window.innerWidth || 1) / (window.innerHeight || 1));
      let hangarFOV = hangarBaseFOV;
      if (hangarAspect < 1.2) {
        const refAspect = 16 / 9;
        const hFovRad = 2 * Math.atan(Math.tan((hangarBaseFOV / 2) * (Math.PI / 180)) * refAspect);
        hangarFOV = Math.min(70, Math.max(hangarBaseFOV, THREE.MathUtils.radToDeg(2 * Math.atan(Math.tan(hFovRad / 2) / hangarAspect))));
      }
      camera = new THREE.PerspectiveCamera(hangarFOV, hangarAspect, 0.05, 100);
        camera.position.set(-6, 2.45, 9.25);
        camera.lookAt(0, 1.0, -1.0);

        scene.add(new THREE.AmbientLight(0x9fc4ff, 0.95));
        const key = new THREE.DirectionalLight(0xffffff, 2.2);
        key.position.set(-3.5, 5.5, 6.5);
        scene.add(key);
        const fill = new THREE.DirectionalLight(0xffe2b6, 0.85);
        fill.position.set(3.2, 2.5, 4.0);
        scene.add(fill);
        const rim = new THREE.DirectionalLight(0x47dcff, 1.8);
        rim.position.set(5.5, 2.8, -4.5);
        scene.add(rim);
        const redAlarm = new THREE.PointLight(0xff3030, 2.6, 11);
        redAlarm.position.set(-4.5, 3.8, 2.5);
        scene.add(redAlarm);
        const amberAlarm = new THREE.PointLight(0xffa21a, 2.2, 10);
        amberAlarm.position.set(4.6, 3.2, 1.8);
        scene.add(amberAlarm);

makeHangar();
      updateSize();
      window.addEventListener("resize", updateSize);
      if (window.visualViewport) window.visualViewport.addEventListener("resize", updateSize);
      if (screen?.orientation) screen.orientation.addEventListener("change", updateSize);

        const speakerUrls = new Map();
        for (const step of list) {
          const speakerId = normalizeSpeakerId(step?.speakerId);
          const slot = stageSlots[speakerId];
          const modelUrl = step?.modelUrl || step?.glbUrl || null;
          if (slot && modelUrl && !speakerUrls.has(speakerId)) speakerUrls.set(speakerId, modelUrl);
        }
        overlay.dataset.expectedModels = String(speakerUrls.size);
        overlay.dataset.loadedModels = "0";

        for (const [speakerId, modelUrl] of speakerUrls.entries()) {
          if (modelUrl) makePlaceholderModel(speakerId, stageSlots[speakerId]);
        }

        overlay.dataset.stageStatus = "ready";
        loading.textContent = `STREAMING CAST 0/${speakerUrls.size}`;
        modelLoadPromise = streamHeroModels(speakerUrls);
        return true;
      } catch (error) {
        overlay.dataset.stageStatus = "failed";
        window.__hangarIntroDebug = {
          message: error?.message || String(error),
          stack: error?.stack || "",
        };
        console.warn("[hangar-intro] 3D stage unavailable", error);
        loading.textContent = "HANGAR MODEL STAGE UNAVAILABLE";
        return false;
      }
    };

    const updateCameraForStep = (elapsedSeconds) => {
      if (!camera) return;
      const activeModel = activeSpeakerId ? loadedModels.get(activeSpeakerId) : null;
      if (activeModel) {
        const slot = activeModel.position;
        const side = slot.x >= 0 ? 1 : -1;
        reusableVectors.desiredCamera.set(slot.x + side * 0.92, 1.95, 6.15);
        reusableVectors.desiredLook.set(slot.x, 1.08, slot.z);
      } else {
        const sweep = Math.sin(elapsedSeconds * 0.28 + currentStepIndex * 0.65);
        reusableVectors.desiredCamera.set(sweep * 4.6, 2.35, 8.6);
        reusableVectors.desiredLook.set(0, 1.0, -1.65);
      }
      camera.position.lerp(reusableVectors.desiredCamera, 0.035);
      reusableVectors.cameraTarget.lerp(reusableVectors.desiredLook, 0.06);
      camera.lookAt(reusableVectors.cameraTarget);
    };

    const updateModelFocus = (elapsedSeconds) => {
      const scaleVector = new THREE.Vector3();
      for (const [speakerId, model] of loadedModels.entries()) {
        const isActive = speakerId === activeSpeakerId;
        const targetScale = isActive ? 1.08 : 0.96;
        const bob = isActive ? Math.sin(elapsedSeconds * 3.0) * 0.035 : 0;
        scaleVector.set(targetScale, targetScale, targetScale);
        model.scale.lerp(scaleVector, 0.07);
        model.position.y = (model.userData.baseY || 0) + bob;
        model.rotation.y = THREE.MathUtils.lerp(
          model.rotation.y,
          Number(model.userData.baseRotationY) || 0,
          0.08
        );
        model.traverse?.((child) => {
          if (!child?.isMesh) return;
          const materials = Array.isArray(child.material) ? child.material : [child.material];
          for (const material of materials) {
            if (!material || !("emissiveIntensity" in material)) continue;
            material.emissiveIntensity = THREE.MathUtils.lerp(
              material.emissiveIntensity || 0,
              isActive ? 0.18 : 0.02,
              0.08
            );
          }
        });
      }
    };

    const animate = (timestamp) => {
      if (closed || !renderer || !scene || !camera) return;
      const elapsedSeconds = timestamp * 0.001;
      updateCameraForStep(elapsedSeconds);
      updateModelFocus(elapsedSeconds);
      renderer.render(scene, camera);
      animationFrame = requestAnimationFrame(animate);
    };

    const setStep = (step, index) => {
      if (loadedModels.size > 0 && overlay.dataset.stageStatus === "loading") {
        overlay.dataset.loadedModels = String(loadedModels.size);
        overlay.dataset.stageStatus = "ready";
      }
      currentStepIndex = index;
      activeSpeakerId = normalizeSpeakerId(step?.speakerId);
      const speakerName = step?.speakerName || speakerNames[activeSpeakerId] || String(step?.speakerId || "").toUpperCase();
      const cardLines = Array.isArray(step?.lines) ? step.lines.filter(Boolean) : [];
      speaker.textContent = step?.kind === "card" ? (step?.title || title) : speakerName;
      metadata.textContent = step?.note || step?.title || "";
      dialogue.textContent = step?.kind === "card"
        ? [step?.title, ...cardLines].filter(Boolean).join("\n")
        : String(step?.text || "");
      plate.style.borderTopColor = activeSpeakerId === "narrator"
        ? "rgba(255, 64, 64, 0.82)"
        : "rgba(126, 232, 255, 0.78)";
    };

    const getStepDuration = (step) => {
      const text = step?.kind === "card"
        ? [step?.title, ...(Array.isArray(step?.lines) ? step.lines : [])].filter(Boolean).join(" ")
        : String(step?.text || "");
      const baseMs = step?.kind === "card" ? 2600 : 2300;
      return Math.max(baseMs, Math.min(6200, baseMs + text.length * 38));
    };

    const resolveAdvance = (action = "next") => {
      if (timerId) clearTimeout(timerId);
      timerId = null;
      resolver?.(action);
    };

    const waitAdvance = (step) =>
      new Promise((resolve) => {
        resolver = resolve;
        if (autoAdvance) {
          timerId = setTimeout(() => resolveAdvance("next"), getStepDuration(step));
        }
      });

    const onClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      resolveAdvance("next");
    };

    const onKeyDown = (event) => {
      const isAdvance = event.code === "Enter" || event.code === "Space";
      const isSkip = event.code === "Escape";
      if (!isAdvance && !isSkip) return;
      event.preventDefault();
      event.stopPropagation();
      event.stopImmediatePropagation?.();
      if (isSkip && skippable) {
        resolveAdvance("skip");
        return;
      }
      resolveAdvance("next");
    };

    overlay.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown, true);

    try {
      const initialized = await initThree();
      if (initialized) animationFrame = requestAnimationFrame(animate);

      for (let stepIndex = 0; stepIndex < list.length; stepIndex++) {
        const step = list[stepIndex];
        setStep(step, stepIndex);
        const action = await waitAdvance(step);
        if (action === "skip") break;
      }
    } finally {
      closed = true;
      if (timerId) clearTimeout(timerId);
      if (animationFrame) cancelAnimationFrame(animationFrame);
      overlay.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKeyDown, true);
      window.removeEventListener("resize", updateSize);
      if (window.visualViewport) window.visualViewport.removeEventListener("resize", updateSize);
      if (screen?.orientation) screen.orientation.removeEventListener("change", updateSize);
      modelLoadPromise?.catch?.(() => {});
      modelLoadPromise = null;
      for (const model of loadedModels.values()) disposeObject(model);
      loadedModels.clear();
      renderer?.dispose?.();
      overlay.remove();
      this.setHUDVisible?.(true);
      try {
        this.detectDeviceType?.();
        this.applyResponsiveLayout?.();
        requestAnimationFrame?.(() => this.applyResponsiveLayout?.());
      } catch {
        // ignore
      }
    }

    return true;
  }

async showCinematicSequence(options = {}) {
    this.detectDeviceType?.();
    const { steps = [], title = "CINEMATIC", subtitle = "", skippable = true } = options;
	    const list = Array.isArray(steps) ? steps.filter(Boolean) : [];
	    if (!list.length) return;

	    const normalizeSpeakerIdForArt = (value) =>
	      String(value || "")
	        .toLowerCase()
	        .replace(/[^a-z0-9]/g, "");

    const existing = document.getElementById("cinematic-overlay");
    if (existing) existing.remove();

    // Hide HUD during cinematics for readability.
    this.setHUDVisible?.(false);

    const overlay = document.createElement("div");
overlay.id = "cinematic-overlay";
      overlay.style.cssText = `
position: fixed; top: 0; left: 0;
width: 100vw; height: 100vh; height: 100dvh;
background: rgba(0, 0, 0, 0.92);
display: flex; justify-content: center; align-items: center;
z-index: 12500;
font-family: 'JetBrains Mono', monospace;
color: #00ffff;
backdrop-filter: blur(8px);
pointer-events: auto;
`;

      const isNarrowCin = this._narrowViewport;

      const frame = document.createElement("div");
frame.style.cssText = `
      width: ${isNarrowCin ? 'calc(100vw - 16px)' : 'min(980px, calc(100vw - 28px))'};
      height: ${isNarrowCin ? 'min(92dvh, 620px)' : 'min(620px, calc(100vh - 28px))'};
      max-height: 100dvh;
      border: 2px solid rgba(0, 255, 255, 0.85);
      background: rgba(0, 20, 40, 0.45);
      box-shadow: 0 0 60px rgba(0, 255, 255, 0.25), inset 0 0 26px rgba(0, 255, 255, 0.08);
      display: grid;
      grid-template-rows: auto 1fr auto;
      overflow: ${isNarrowCin ? 'auto' : 'hidden'};
      position: relative;
    `;

      const header = document.createElement("div");
    header.style.cssText = `
      padding: ${isNarrowCin ? '8px 12px 6px 12px' : '14px 18px 10px 18px'};
      border-bottom: 1px solid rgba(0, 255, 255, 0.35);
      background: linear-gradient(180deg, rgba(0, 40, 70, 0.30), rgba(0, 20, 40, 0.0));
    `;
    header.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;">
        <div style="font-size: ${isNarrowCin ? '14px' : '18px'}; letter-spacing: 3px; font-weight: bold;">▌ ${title}</div>
        <div style="font-size: 11px; color: #77c; opacity: 0.85;">TRANSMISSION // SECURE</div>
      </div>
      <div style="margin-top: 6px; color: rgba(160, 255, 255, 0.95); font-size: 12px;">${subtitle}</div>
    `;

const body = document.createElement("div");
      body.style.cssText = `
display: grid;
grid-template-columns: ${isNarrowCin ? '1fr' : '360px 1fr'};
gap: 16px;
padding: ${isNarrowCin ? '10px' : '18px'};
min-height: 0;
align-items: stretch;
`;

const modelBox = document.createElement("div");
      const mSize = isNarrowCin ? Math.min(200, Math.round((window.innerWidth || 1) * 0.45)) : 360;
      modelBox.style.cssText = `
width: ${mSize}px;
height: ${mSize}px;
border: 2px solid rgba(0, 255, 255, 0.75);
box-shadow: 0 0 22px rgba(0, 255, 255, 0.18), inset 0 0 22px rgba(0, 255, 255, 0.08);
background: rgba(0, 0, 0, 0.25);
display: grid;
place-items: center;
overflow: hidden;
position: relative;
${isNarrowCin ? 'margin: 0 auto;' : ''}
`;

	    const modelCanvas = document.createElement("canvas");
	      const mCanvasRes = isNarrowCin ? 280 : 520;
	      modelCanvas.width = mCanvasRes;
	      modelCanvas.height = mCanvasRes;
	    modelCanvas.style.cssText = `
width: ${mSize}px;
height: ${mSize}px;
	      position: absolute;
	      top: 0;
	      left: 0;
	      display: none;
	      pointer-events: none;
	    `;

	    const modelImg = document.createElement("img");
	    modelImg.alt = "Character portrait";
	    modelImg.decoding = "async";
	    modelImg.loading = "lazy";
	    modelImg.style.cssText = `
width: ${mSize}px;
height: ${mSize}px;
	      position: absolute;
	      top: 0;
	      left: 0;
	      display: none;
	      object-fit: contain;
	      image-rendering: pixelated;
	      pointer-events: none;
	    `;

	    const modelPlaceholder = document.createElement("div");
	    modelPlaceholder.style.cssText = `
	      font-size: 54px;
	      color: rgba(0,255,255,0.9);
      opacity: 0.9;
    `;
	    modelPlaceholder.textContent = "◼";

    const modelLoading = document.createElement("div");
    modelLoading.style.cssText = `
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      display: none;
      padding: 8px 10px;
      font-size: 10px;
      letter-spacing: 1px;
      color: rgba(180, 255, 180, 0.95);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.62));
      pointer-events: none;
      text-align: center;
    `;
    modelLoading.textContent = "LOADING MODEL…";

    modelBox.appendChild(modelCanvas);
    modelBox.appendChild(modelImg);
    modelBox.appendChild(modelPlaceholder);
    modelBox.appendChild(modelLoading);

    const textArea = document.createElement("div");
    textArea.style.cssText = `
      border: 1px solid rgba(0, 255, 255, 0.25);
      background: rgba(0, 0, 0, 0.18);
      padding: ${isNarrowCin ? '8px 10px' : '16px 16px'};
      min-height: 0;
      overflow: ${isNarrowCin ? 'auto' : 'hidden'};
      display: flex;
      flex-direction: column;
    `;

    const sceneTitle = document.createElement("div");
    sceneTitle.style.cssText = "font-size: 10px; color: rgba(160,255,255,0.75); letter-spacing: 2px;";
    const speakerLine = document.createElement("div");
    speakerLine.style.cssText = "margin-top: 10px; font-size: 16px; letter-spacing: 2px; font-weight: bold;";
    const noteLine = document.createElement("div");
    noteLine.style.cssText = "margin-top: 10px; font-size: 11px; color: rgba(200,255,255,0.75);";
    const dialogueLine = document.createElement("div");
    dialogueLine.style.cssText =
      `margin-top: 14px; font-size: ${isNarrowCin ? '12px' : '14px'}; color: rgba(220,255,220,0.95); line-height: 1.6; white-space: pre-line; word-break: break-word; overflow-y: auto; flex: 1 1 auto; padding-right: 6px;`;

    textArea.appendChild(sceneTitle);
    textArea.appendChild(speakerLine);
    textArea.appendChild(noteLine);
    textArea.appendChild(dialogueLine);

    const footer = document.createElement("div");
    footer.style.cssText = `
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding: ${isNarrowCin ? '6px 12px' : '10px 18px'};
      border-top: 1px solid rgba(0, 255, 255, 0.25);
      background: rgba(0, 0, 0, 0.18);
      color: rgba(200,255,255,0.85);
      font-size: 11px;
    `;
    const hint = document.createElement("div");
    hint.textContent = skippable
      ? "Click/Enter: advance • Esc: skip"
      : "Click/Enter: advance";
    const stepCount = document.createElement("div");
    stepCount.style.opacity = "0.8";
    footer.appendChild(hint);
    footer.appendChild(stepCount);

    overlay.appendChild(frame);
    frame.appendChild(header);
    frame.appendChild(body);
    body.appendChild(modelBox);
    body.appendChild(textArea);
    frame.appendChild(footer);
    document.body.appendChild(overlay);

    const cinematic3d = {
      renderer: null,
      scene: null,
      camera: null,
      modelRoot: null,
      animId: null,
      lastTs: 0,
      url: null,
      requestId: 0,
      gl: null,
    };

    const disposeObject = (obj) => {
      if (!obj) return;
      obj.traverse?.((child) => {
        if (child?.geometry && typeof child.geometry.dispose === "function") child.geometry.dispose();
        if (child?.material) {
          const disposeMaterial = (material) => {
            if (!material) return;
            if (Array.isArray(material)) return material.forEach(disposeMaterial);
            for (const key of ["map", "emissiveMap", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "alphaMap"]) {
              const tex = material[key];
              if (tex && typeof tex.dispose === "function") tex.dispose();
            }
            if (typeof material.dispose === "function") material.dispose();
          };
          disposeMaterial(child.material);
        }
      });
    };

	    const stopModelPreview = () => {
	      cinematic3d.requestId++;
	      if (cinematic3d.animId) cancelAnimationFrame(cinematic3d.animId);
	      cinematic3d.animId = null;
	      cinematic3d.lastTs = 0;

      if (cinematic3d.scene && cinematic3d.modelRoot) {
        try {
          cinematic3d.scene.remove(cinematic3d.modelRoot);
        } catch {
          // ignore
        }
      }
      if (cinematic3d.modelRoot) {
        try {
          disposeObject(cinematic3d.modelRoot);
        } catch {
          // ignore
        }
      }
      cinematic3d.modelRoot = null;
      cinematic3d.url = null;

      if (cinematic3d.renderer) {
        try {
          cinematic3d.renderer.dispose?.();
        } catch {
          // ignore
        }
      }
      cinematic3d.renderer = null;
	      cinematic3d.scene = null;
	      cinematic3d.camera = null;
	      cinematic3d.gl = null;
	      modelCanvas.style.display = "none";
	      modelLoading.style.display = "none";
	      modelImg.style.display = "none";
	      modelPlaceholder.style.display = "";
	    };

	    const setModelFallbackFromArt = (art) => {
	      const url = art?.profileVariants?.neutral || art?.profileUrl || null;
	      if (!url) {
	        modelImg.style.display = "none";
	        modelPlaceholder.style.display = "";
	        return;
	      }
	      if (modelImg.src !== url) modelImg.src = url;
	      modelImg.style.display = "";
	      modelPlaceholder.style.display = "none";
	    };

	    const ensureWebGL = () => {
	      const ctx = getThreeWebGLContext(modelCanvas, { alpha: true, antialias: true, premultipliedAlpha: false });
	      if (!ctx) return false;
	      cinematic3d.gl = ctx;
	      return true;
    };

    const startModelPreview = async (glbUrl) => {
      if (!glbUrl) return false;

      // Avoid re-init if already showing the same GLB.
      if (cinematic3d.url === glbUrl && cinematic3d.animId) return true;

      stopModelPreview();
      if (!ensureWebGL()) return false;

      const requestId = cinematic3d.requestId;
      cinematic3d.url = glbUrl;
      modelLoading.textContent = "LOADING MODEL…";
      modelLoading.style.display = "block";

      let root = null;
      try {
        const loader = await getConfiguredGLTFLoader();
        const gltf = await new Promise((resolve, reject) => {
          loader.load(
            glbUrl,
            resolve,
            (evt) => {
              if (cinematic3d.requestId !== requestId) return;
              const loaded = Number(evt?.loaded) || 0;
              const total = Number(evt?.total) || 0;
              if (total > 0) {
                const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
                modelLoading.textContent = `LOADING MODEL… ${pct}%`;
              } else if (loaded > 0) {
                const mb = Math.max(0, loaded / (1024 * 1024));
                modelLoading.textContent = `LOADING MODEL… ${mb.toFixed(1)}MB`;
              }
            },
            reject
          );
        });

        if (cinematic3d.requestId !== requestId) return false;

        root = gltf?.scene || gltf?.scenes?.[0];
        if (!root) return false;

        cinematic3d.renderer = new THREE.WebGLRenderer({
          canvas: modelCanvas,
          context: cinematic3d.gl,
          alpha: true,
          antialias: true,
        });
        cinematic3d.renderer.setPixelRatio(1);
        cinematic3d.renderer.outputColorSpace = THREE.SRGBColorSpace;
        cinematic3d.renderer.setClearColor(0x000000, 0);
        cinematic3d.renderer.setSize(modelCanvas.width, modelCanvas.height, false);

        cinematic3d.scene = new THREE.Scene();
        cinematic3d.camera = new THREE.PerspectiveCamera(35, modelCanvas.width / modelCanvas.height, 0.01, 100);

        cinematic3d.scene.add(new THREE.AmbientLight(0xffffff, 1.2));
        const key = new THREE.DirectionalLight(0xffffff, 1.0);
        key.position.set(2, 3, 4);
        cinematic3d.scene.add(key);
        const rim = new THREE.DirectionalLight(0x00ffff, 0.65);
        rim.position.set(-3, 1, -2);
        cinematic3d.scene.add(rim);

        // Center + scale to fit.
        const box = new THREE.Box3().setFromObject(root);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
        root.position.sub(center);
        root.scale.setScalar(1.55 / maxDim);

        root.traverse((obj) => {
          if (!obj?.isMesh) return;
          const material = obj.material;
          if (material && typeof material === "object") {
            material.side = THREE.DoubleSide;
            if ("metalness" in material) material.metalness = 0;
            if ("roughness" in material) material.roughness = 1;
            material.needsUpdate = true;
          }
        });

        cinematic3d.modelRoot = root;
        cinematic3d.scene.add(root);

        cinematic3d.camera.position.set(0, 0.25, 2.35);
        cinematic3d.camera.lookAt(0, 0.15, 0);

	        modelCanvas.style.display = "";
	        modelPlaceholder.style.display = "none";
	        modelImg.style.display = "none";
	        modelLoading.style.display = "none";
	      } catch {
	        stopModelPreview();
	        return false;
	      }

      const animate = (ts) => {
        if (!document.getElementById("cinematic-overlay") || cinematic3d.url !== glbUrl) {
          stopModelPreview();
          return;
        }
        const dt = cinematic3d.lastTs ? Math.min(0.05, (ts - cinematic3d.lastTs) / 1000) : 0.016;
        cinematic3d.lastTs = ts;
        if (cinematic3d.modelRoot) cinematic3d.modelRoot.rotation.y += dt * 0.65;
        cinematic3d.renderer.render(cinematic3d.scene, cinematic3d.camera);
        cinematic3d.animId = requestAnimationFrame(animate);
      };

      cinematic3d.animId = requestAnimationFrame(animate);
      return true;
    };

    let typing = false;
    let typingToken = 0;

    const setDialogue = async ({ titleText = "", speakerText = "", noteText = "", bodyText = "" } = {}) => {
      sceneTitle.textContent = titleText || "";
      speakerLine.textContent = speakerText || "";
      noteLine.textContent = noteText || "";

      typing = true;
      typingToken += 1;
      const token = typingToken;
      dialogueLine.textContent = "";
      const text = String(bodyText || "");
      const speedMs = 12;

      for (let i = 0; i < text.length; i++) {
        if (typingToken !== token) break;
        dialogueLine.textContent += text[i];
        // Skip delays on whitespace.
        if (text[i] === " " || text[i] === "\n") continue;
        // eslint-disable-next-line no-await-in-loop
        await new Promise((r) => setTimeout(r, speedMs));
      }

      if (typingToken === token) dialogueLine.textContent = text;
      typing = false;
    };

    const advance = () => {
      if (typing) {
        typingToken += 1;
        typing = false;
        return "reveal";
      }
      return "next";
    };

    let closed = false;
    let skipped = false;
    let resolver = null;
    const waitAdvance = () =>
      new Promise((resolve) => {
        resolver = resolve;
      });

    const onClick = (e) => {
      e.preventDefault();
      e.stopPropagation();
      const res = advance();
      if (res === "next") resolver?.("next");
    };

    const onKeyDown = (e) => {
      const code = e.code;
      const isAdvance = code === "Enter" || code === "Space";
      const isSkip = code === "Escape";
      if (!isAdvance && !isSkip) return;
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();
      if (isSkip && skippable) {
        skipped = true;
        resolver?.("skip");
        return;
      }
      const res = advance();
      if (res === "next") resolver?.("next");
    };

    overlay.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKeyDown, true);

    const enable3dModelPreview = !!options?.enable3dModelPreview;

    try {
      for (let i = 0; i < list.length; i++) {
        const step = list[i];
        stepCount.textContent = `${i + 1}/${list.length}`;

        if (step?.kind === "card") {
          stopModelPreview();
          const lines = Array.isArray(step.lines) ? step.lines : [];
          const text = [step.title, ...lines].filter(Boolean).join("\n");
          await setDialogue({
            titleText: "",
            speakerText: "",
            noteText: "",
            bodyText: text,
          });
          const action = await waitAdvance();
          if (action === "skip") break;
          continue;
        }

        const speakerId = String(step?.speakerId || "");
        const resolveSpeakerName = (id) =>
          ({
            narrator: "NARRATOR",
            admiralbulldog: "ADMIRAL BULLDOG",
            admiralbloodhound: "ADMIRAL BULLDOG",
            admiral: "ADMIRAL BULLDOG",
            logi7: "LOGI-7",
            civilianpilot: "CIVILIAN PILOT",
            acquisitioncaptain: "ACQUISITION CAPTAIN",
            marketmakeromega: "MARKET MAKER OMEGA",
            honeybadger: "RAGEBURROW",
            octopus: "MULTIVEIL",
            elephant: "GRAY MATTER",
            mantisshrimp: "CHROMA WAR",
            ant: "FULFILLMENT QUEEN",
          }[String(id || "").toLowerCase()] || String(id || "UNKNOWN").toUpperCase());
        const speakerName = step?.speakerName || (speakerId ? resolveSpeakerName(speakerId) : "");
        const titleText = step?.title || "";
        const noteText = step?.note || "";
        const bodyText = step?.text || "";

	        const showModel = step?.showModel !== false;
	        if (showModel) {
	          const artId = normalizeSpeakerIdForArt(speakerId);
	          const art = getCharacterArt(artId);
	          stopModelPreview();
            const glbUrl =
              step?.glbUrl ||
              step?.modelUrl ||
              art?.fullProfileGlbUrl ||
              art?.characterGlbUrl ||
              null;

            let shown3d = false;
            if (enable3dModelPreview && glbUrl) {
              shown3d = await startModelPreview(glbUrl);
            }

            if (!shown3d) setModelFallbackFromArt(art);
	        } else {
	          stopModelPreview();
	          setModelFallbackFromArt(null);
	        }

        await setDialogue({
          titleText,
          speakerText: speakerName ? `▌ ${speakerName}` : "",
          noteText,
          bodyText,
        });

        const action = await waitAdvance();
        if (action === "skip") break;
      }
	    } finally {
	      if (closed) return;
	      closed = true;
	      overlay.removeEventListener("click", onClick, true);
	      window.removeEventListener("keydown", onKeyDown, true);
	      stopModelPreview();
	      overlay.remove();
	      this.setHUDVisible?.(true);
	      // Cinematics temporarily hide the HUD; re-apply layout on restore so we
	      // don't end up stuck in the stacked/mobile layout after viewport changes.
	      try {
	        this.detectDeviceType?.();
	        this.applyResponsiveLayout?.();
	        requestAnimationFrame?.(() => this.applyResponsiveLayout?.());
	      } catch {
	        // ignore
	      }
	    }

    return skipped;
  }

async showCharacterSelect(options = {}) {
    const { profiles = [], selectedId = null, title = "CONTRACTOR SELECTION", subtitle = "Pick a contractor profile. Deploy to launch the contract.", } = options;

    this.detectDeviceType?.();

    const list = Array.isArray(profiles) ? profiles.slice() : [];
    if (!list.length) return selectedId || null;

    const normalizedSelected = String(selectedId || list[0]?.id || "").toLowerCase();
    let activeIndex = Math.max(
      0,
      list.findIndex((p) => String(p?.id || "").toLowerCase() === normalizedSelected)
    );
    if (!Number.isFinite(activeIndex) || activeIndex < 0) activeIndex = 0;

    const existing = document.getElementById("character-select-overlay");
    if (existing) existing.remove();

    const overlay = document.createElement("div");
overlay.id = "character-select-overlay";
      overlay.style.cssText = `
position: fixed; top: 0; left: 0;
width: 100vw; height: 100vh; height: 100dvh;
background: rgba(0, 5, 15, 0.92);
display: flex; justify-content: center; align-items: center;
z-index: 12000;
font-family: 'JetBrains Mono', monospace;
color: #00ffff;
backdrop-filter: blur(8px);
pointer-events: auto;
`;

const frame = document.createElement("div");
      const isNarrow = this._narrowViewport;
      frame.style.cssText = `
width: ${isNarrow ? 'calc(100vw - 16px)' : 'min(1040px, calc(100vw - 28px))'};
height: ${isNarrow ? '100dvh' : 'min(680px, calc(100vh - 28px))'};
max-height: 100dvh;
border: 2px solid rgba(0, 255, 255, 0.85);
background: rgba(0, 20, 40, 0.78);
box-shadow: 0 0 60px rgba(0, 255, 255, 0.25), inset 0 0 26px rgba(0, 255, 255, 0.08);
display: grid;
grid-template-rows: auto 1fr auto;
overflow: ${isNarrow ? 'auto' : 'hidden'};
position: relative;
`;

    const header = document.createElement("div");
    header.style.cssText = `
      padding: 18px 18px 12px 18px;
      border-bottom: 1px solid rgba(0, 255, 255, 0.35);
      background: linear-gradient(180deg, rgba(0, 40, 70, 0.25), rgba(0, 20, 40, 0.0));
    `;
header.innerHTML = `
      <div style="display:flex;justify-content:space-between;align-items:baseline;gap:10px;">
        <div style="font-size: ${isNarrow ? '15px' : '20px'}; letter-spacing: 3px; font-weight: bold;">▌ ${title}</div>
        <div style="font-size: 11px; color: #77c; opacity: 0.85;">BOARDROOM NEBULA // PRE-LAUNCH</div>
      </div>
      <div style="margin-top: 6px; color: rgba(160, 255, 255, 0.95); font-size: ${isNarrow ? '11px' : '12px'};">${subtitle}</div>
      <div style="margin-top: 8px; color: rgba(180, 255, 180, 0.9); font-size: 11px;${isNarrow ? 'display:none;' : ''}">
        Controls: ↑/↓ or W/S to navigate • Enter to deploy • Esc to deploy current
      </div>
      `;

const body = document.createElement("div");
      body.style.cssText = `
display: grid;
grid-template-columns: ${isNarrow ? '1fr' : '360px 1fr'};
height: ${isNarrow ? 'auto' : '100%'};
min-height: 0;
`;

const left = document.createElement("div");
left.style.cssText = `
      border-right: ${isNarrow ? 'none' : '1px solid rgba(0, 255, 255, 0.25)'};
      border-bottom: ${isNarrow ? '1px solid rgba(0, 255, 255, 0.25)' : 'none'};
      padding: ${isNarrow ? '10px' : '14px'};
      overflow: auto;
      ${isNarrow ? 'max-height: 55dvh;' : ''}
    `;

const right = document.createElement("div");
      right.style.cssText = `
padding: ${isNarrow ? '12px' : '18px'};
overflow: auto;
min-height: 0;
`;

const portraitBox = document.createElement("div");
      const pSize = isNarrow ? 100 : 180;
portraitBox.style.cssText = `
      width: ${pSize}px;
      height: ${pSize}px;
      border: 2px solid rgba(0, 255, 255, 0.75);
      box-shadow: 0 0 22px rgba(0, 255, 255, 0.18), inset 0 0 22px rgba(0, 255, 255, 0.08);
      background: rgba(0, 0, 0, 0.35);
      display: grid;
      place-items: center;
      overflow: ${isNarrow ? 'visible' : 'hidden'};
      flex: 0 0 auto;
      position: relative;
    `;

    const portraitImg = document.createElement("img");
portraitImg.alt = "Contractor portrait";
      portraitImg.style.cssText = `
width: ${pSize}px;
height: ${pSize}px;
object-fit: cover;
image-rendering: pixelated;
display: none;
`;

const portraitCanvas = document.createElement("canvas");
      const canvasRes = isNarrow ? 140 : 220;
      portraitCanvas.width = canvasRes;
      portraitCanvas.height = canvasRes;
      portraitCanvas.style.cssText = `
width: ${pSize}px;
height: ${pSize}px;
display: none;
`;

    const portraitIcon = document.createElement("div");
portraitIcon.style.cssText = `
font-size: ${isNarrow ? '36px' : '64px'};
line-height: 1;
opacity: 0.95;
`;

    const portraitLoading = document.createElement("div");
    portraitLoading.style.cssText = `
      position: absolute;
      left: 0;
      right: 0;
      bottom: 0;
      display: none;
      place-items: center;
      padding: 8px 10px;
      font-size: 10px;
      letter-spacing: 1px;
      color: rgba(180, 255, 180, 0.95);
      background: linear-gradient(180deg, rgba(0, 0, 0, 0.0), rgba(0, 0, 0, 0.62));
      pointer-events: none;
      text-align: center;
    `;
    portraitLoading.textContent = "3D PREVIEW LOADING…";

    portraitBox.appendChild(portraitCanvas);
    portraitBox.appendChild(portraitImg);
    portraitBox.appendChild(portraitIcon);
    portraitBox.appendChild(portraitLoading);

const footer = document.createElement("div");
      footer.style.cssText = `
display: flex;
flex-direction: ${isNarrow ? 'column' : 'row'};
justify-content: space-between;
align-items: center;
padding: ${isNarrow ? '8px 10px' : '12px 18px'};
gap: ${isNarrow ? '8px' : '0'};
border-top: 1px solid rgba(0, 255, 255, 0.25);
background: rgba(0, 0, 0, 0.18);
`;

    const hint = document.createElement("div");
    hint.style.cssText = `font-size: 11px; color: rgba(200, 255, 255, 0.9);${isNarrow ? 'display:none;' : ''}`;
    hint.textContent = "Tip: Art placeholders now; swap in portraits/GLBs later via CharacterArt + profiles.";

    const actions = document.createElement("div");
    actions.style.cssText = `display:flex;gap:10px;align-items:center;`;

    const deployBtn = document.createElement("button");
    deployBtn.type = "button";
    deployBtn.textContent = "DEPLOY";
    deployBtn.style.cssText = `
      padding: 10px 14px;
      border: 1px solid rgba(0, 255, 255, 0.85);
      background: rgba(0, 255, 255, 0.08);
      color: #00ffff;
      letter-spacing: 2px;
      font-weight: bold;
      cursor: pointer;
      box-shadow: 0 0 16px rgba(0, 255, 255, 0.12);
    `;

    const randomBtn = document.createElement("button");
    randomBtn.type = "button";
    randomBtn.textContent = "RANDOMIZE";
    randomBtn.style.cssText = `
      padding: 10px 12px;
      border: 1px solid rgba(0, 255, 0, 0.55);
      background: rgba(0, 255, 0, 0.06);
      color: #66ff66;
      letter-spacing: 1px;
      cursor: pointer;
    `;

    actions.appendChild(randomBtn);
    actions.appendChild(deployBtn);
    footer.appendChild(hint);
    footer.appendChild(actions);

    const personalityBar = (label, value, color) => {
      const v = Math.max(0, Math.min(100, Number(value) || 0));
      const row = document.createElement("div");
      row.style.cssText = `margin: 10px 0;`;
      row.innerHTML = `
        <div style="display:flex;justify-content:space-between;font-size:11px;color:rgba(200,255,255,0.9);">
          <span>${label}</span><span style="color:${color};">${v}%</span>
        </div>
        <div style="height: 10px; background: rgba(0,0,0,0.35); border: 1px solid rgba(0,255,255,0.25); margin-top: 4px;">
          <div style="height: 100%; width: ${v}%; background: linear-gradient(90deg, ${color}, rgba(255,255,255,0.2)); box-shadow: 0 0 16px rgba(0,255,255,0.08);"></div>
        </div>
      `;
      return row;
    };

    const cycleOrder = ["neutral", "smug", "angry", "shocked", "damaged"];
    let cycleTimer = null;
    let cycleUrls = [];
    let cycleIndex = 0;
    let cycleCharacterId = null;
    let scheduledGlbStartToken = 0;

    // 3D full-profile preview (GLB) state
    const glbPreview = {
      url: null,
      renderer: null,
      scene: null,
      camera: null,
      modelRoot: null,
      animId: null,
      lastTs: 0,
      gl: null,
      isLoading: false,
      requestId: 0,
    };
    // Hide the "loading" ribbon after a bit so big GLBs don't look like a hard-stuck UI.
    // (The GLB can still finish loading later and will swap in when ready.)
    const GLB_PREVIEW_TIMEOUT_MS = 12000;

    const stopPortraitCycle = () => {
      if (cycleTimer) {
        clearInterval(cycleTimer);
        cycleTimer = null;
      }
      cycleUrls = [];
      cycleIndex = 0;
      cycleCharacterId = null;
    };

    const disposeObject = (obj) => {
      if (!obj) return;
      obj.traverse?.((child) => {
        if (child?.geometry && typeof child.geometry.dispose === "function") child.geometry.dispose();
        if (child?.material) {
          const disposeMaterial = (material) => {
            if (!material) return;
            if (Array.isArray(material)) {
              material.forEach(disposeMaterial);
              return;
            }
            for (const key of ["map", "emissiveMap", "normalMap", "roughnessMap", "metalnessMap", "aoMap", "alphaMap"]) {
              const tex = material[key];
              if (tex && typeof tex.dispose === "function") tex.dispose();
            }
            if (typeof material.dispose === "function") material.dispose();
          };
          disposeMaterial(child.material);
        }
      });
    };

    const stopGlbPreview = () => {
      glbPreview.requestId++;
      if (glbPreview.animId) {
        cancelAnimationFrame(glbPreview.animId);
        glbPreview.animId = null;
      }
      glbPreview.lastTs = 0;
      glbPreview.isLoading = false;

      if (glbPreview.modelRoot) {
        try {
          disposeObject(glbPreview.modelRoot);
        } catch {
          // ignore
        }
      }

      if (glbPreview.scene && glbPreview.modelRoot) {
        try {
          glbPreview.scene.remove(glbPreview.modelRoot);
        } catch {
          // ignore
        }
      }

      glbPreview.modelRoot = null;
      glbPreview.url = null;

      if (glbPreview.renderer) {
        try {
          glbPreview.renderer.dispose?.();
        } catch {
          // ignore
        }
      }

      glbPreview.renderer = null;
      glbPreview.scene = null;
      glbPreview.camera = null;
      glbPreview.gl = null;
      portraitCanvas.style.display = "none";
      portraitLoading.style.display = "none";
    };

    const hasWebGL = () => {
      const ctx = getThreeWebGLContext(portraitCanvas, { alpha: true, antialias: true, premultipliedAlpha: false });
      if (!ctx) return false;
      glbPreview.gl = ctx;
      return true;
    };

    const startGlbPreviewFor = async (profile) => {
      if (!isArtEnabled()) return false;
      const id = String(profile?.id || "").toLowerCase();
      const art = getCharacterArt(id);
      const glbUrl = art?.fullProfileGlbUrl || null;
      if (!glbUrl) return false;

      // Avoid re-init if already showing the same GLB.
      if (glbPreview.url === glbUrl && glbPreview.animId) return true;

      stopGlbPreview();

      if (!hasWebGL()) return false;

      const requestId = glbPreview.requestId;
      glbPreview.url = glbUrl;
      glbPreview.isLoading = true;
      portraitLoading.style.display = "grid";

      let root = null;
      try {
        const updateLoading = (text) => {
          if (glbPreview.requestId !== requestId) return;
          portraitLoading.textContent = text;
        };

        updateLoading("3D PREVIEW LOADING…");

        const loadPromise = (async () => {
          const loader = await getConfiguredGLTFLoader();
          const startMs = typeof performance !== "undefined" ? performance.now() : Date.now();
          const gltf = await new Promise((resolve, reject) => {
            loader.load(
              glbUrl,
              resolve,
              (evt) => {
                if (glbPreview.requestId !== requestId) return;
                const loaded = Number(evt?.loaded) || 0;
                const total = Number(evt?.total) || 0;
                if (total > 0) {
                  const pct = Math.max(0, Math.min(100, Math.round((loaded / total) * 100)));
                  updateLoading(`3D PREVIEW LOADING… ${pct}%`);
                } else if (loaded > 0) {
                  const mb = Math.max(0, loaded / (1024 * 1024));
                  updateLoading(`3D PREVIEW LOADING… ${mb.toFixed(1)}MB`);
                }
              },
              reject
            );
          });
          const srcRoot = gltf?.scene || gltf?.scenes?.[0];
          if (!srcRoot) throw new Error("GLB contained no scene");
          const endMs = typeof performance !== "undefined" ? performance.now() : Date.now();
          return { srcRoot, ms: Math.max(0, endMs - startMs) };
        })();

        const guardedLoadPromise = loadPromise
          .then((value) => ({ ...value, error: null }))
          .catch((error) => ({ srcRoot: null, ms: 0, error }));

        const hideTimer = setTimeout(() => {
          if (glbPreview.requestId !== requestId) return;
          if (!glbPreview.isLoading) return;
          portraitLoading.style.display = "none";
        }, GLB_PREVIEW_TIMEOUT_MS);

        const result = await guardedLoadPromise;
        clearTimeout(hideTimer);

        if (result?.error) {
          stopGlbPreview();
          return false;
        }

        const { srcRoot } = result || {};
        if (!srcRoot) return false;
        if (glbPreview.requestId !== requestId) return false;

        root = srcRoot;

        if (!root) return false;

        // Create renderer/scene/camera only once the model is ready (keeps UI responsive while loading).
        glbPreview.renderer = new THREE.WebGLRenderer({
          canvas: portraitCanvas,
          context: glbPreview.gl,
          alpha: true,
          antialias: true,
        });
        glbPreview.renderer.setPixelRatio(1);
        glbPreview.renderer.outputColorSpace = THREE.SRGBColorSpace;
        glbPreview.renderer.setClearColor(0x000000, 0);
        glbPreview.renderer.setSize(portraitCanvas.width, portraitCanvas.height, false);

        glbPreview.scene = new THREE.Scene();
        glbPreview.camera = new THREE.PerspectiveCamera(
          35,
          portraitCanvas.width / portraitCanvas.height,
          0.01,
          100
        );

        glbPreview.scene.add(new THREE.AmbientLight(0xffffff, 1.15));
        const key = new THREE.DirectionalLight(0xffffff, 1.0);
        key.position.set(2, 3, 4);
        glbPreview.scene.add(key);
        const rim = new THREE.DirectionalLight(0x00ffff, 0.55);
        rim.position.set(-3, 1, -2);
        glbPreview.scene.add(rim);

        // Center + scale to fit.
        const box = new THREE.Box3().setFromObject(root);
        const center = box.getCenter(new THREE.Vector3());
        const size = box.getSize(new THREE.Vector3());
        const maxDim = Math.max(size.x || 1, size.y || 1, size.z || 1);
        root.position.sub(center);

        const scale = 1.35 / maxDim;
        root.scale.setScalar(scale);

        // Optional per-character rotation override for the full-profile model.
        const rot = art?.fullProfileGlbRotation;
        if (rot && typeof rot === "object") {
          if (Number.isFinite(rot.x)) root.rotation.x += rot.x;
          if (Number.isFinite(rot.y)) root.rotation.y += rot.y;
          if (Number.isFinite(rot.z)) root.rotation.z += rot.z;
        }

        // Make materials consistent in the tiny preview.
        root.traverse((obj) => {
          if (!obj?.isMesh) return;
          const material = obj.material;
          if (material && typeof material === "object") {
            material.side = THREE.DoubleSide;
            if ("metalness" in material) material.metalness = 0;
            if ("roughness" in material) material.roughness = 1;
            material.needsUpdate = true;
          }
        });

        glbPreview.modelRoot = root;
        glbPreview.scene.add(root);

        glbPreview.camera.position.set(0, 0.25, 2.4);
        glbPreview.camera.lookAt(0, 0.15, 0);

        // Swap 2D -> 3D only once ready (prevents “stuck loading” blank portraits).
        stopPortraitCycle();
        portraitCanvas.style.display = "";
        portraitImg.style.display = "none";
        portraitIcon.style.display = "none";
      } catch {
        stopGlbPreview();
        return false;
      } finally {
        glbPreview.isLoading = false;
        portraitLoading.style.display = "none";
        portraitLoading.textContent = "3D PREVIEW LOADING…";
      }

      const animate = (ts) => {
        const overlayEl = document.getElementById("character-select-overlay");
        if (!overlayEl || glbPreview.url !== glbUrl) {
          stopGlbPreview();
          return;
        }

        const dt = glbPreview.lastTs ? Math.min(0.05, (ts - glbPreview.lastTs) / 1000) : 0.016;
        glbPreview.lastTs = ts;

        if (glbPreview.modelRoot) {
          glbPreview.modelRoot.rotation.y += dt * 0.7;
        }
        glbPreview.renderer.render(glbPreview.scene, glbPreview.camera);
        glbPreview.animId = requestAnimationFrame(animate);
      };

      glbPreview.animId = requestAnimationFrame(animate);
      return true;
    };

    const unique = (arr) => Array.from(new Set(arr.filter(Boolean)));

    const startPortraitCycleFor = (profile) => {
      const id = String(profile?.id || "").toLowerCase();
      if (!id) {
        stopPortraitCycle();
        portraitImg.style.display = "none";
        portraitIcon.style.display = "";
        portraitIcon.textContent = profile?.icon || "🚀";
        return;
      }

      const art = getCharacterArt(id);
      const variants = art?.profileVariants || null;
      const urlsFromVariants = variants
        ? cycleOrder.map((k) => variants[k]).filter(Boolean)
        : [];
      const urls = unique([
        ...urlsFromVariants,
        art?.profileUrl,
        art?.profileVariants?.neutral,
      ]);

      stopPortraitCycle();

      if (!urls.length) {
        portraitImg.style.display = "none";
        portraitIcon.style.display = "";
        portraitIcon.textContent = profile?.icon || "🚀";
        portraitCanvas.style.display = "none";
        return;
      }

      cycleUrls = urls;
      cycleIndex = 0;
      cycleCharacterId = id;

      portraitIcon.style.display = "none";
      portraitImg.style.display = "";
      portraitCanvas.style.display = "none";
      portraitImg.src = cycleUrls[cycleIndex];

      if (cycleUrls.length <= 1) return;

      cycleTimer = setInterval(() => {
        // Overlay might be gone; fail-safe.
        const overlayEl = document.getElementById("character-select-overlay");
        if (!overlayEl) {
          stopPortraitCycle();
          return;
        }
        if (cycleCharacterId !== id) return;

        cycleIndex = (cycleIndex + 1) % cycleUrls.length;
        portraitImg.src = cycleUrls[cycleIndex];
      }, 1600);
    };

    const renderLeft = () => {
      left.innerHTML = "";
      list.forEach((p, idx) => {
        const isActive = idx === activeIndex;
        const btn = document.createElement("button");
        btn.type = "button";
        btn.dataset.id = p.id;
        btn.style.cssText = `
          width: 100%;
          text-align: left;
          padding: 12px 12px;
          margin-bottom: 10px;
          border: 1px solid ${isActive ? "rgba(0,255,255,0.9)" : "rgba(0,255,255,0.22)"};
          background: ${isActive ? "rgba(0,255,255,0.08)" : "rgba(0,0,0,0.16)"};
          color: ${isActive ? "#00ffff" : "rgba(200,255,255,0.9)"};
          cursor: pointer;
          box-shadow: ${isActive ? "0 0 22px rgba(0,255,255,0.16)" : "none"};
        `;
        btn.innerHTML = `
          <div style="display:flex;align-items:baseline;justify-content:space-between;gap:10px;">
            <div style="font-size: 14px; letter-spacing: 2px; font-weight: bold;">
              <span style="margin-right: 8px;">${p.icon || "🚀"}</span>${p.name || String(p.id || "").toUpperCase()}
            </div>
            <div style="font-size: 10px; color: rgba(160,255,255,0.7);">${p.ship || ""}</div>
          </div>
          <div style="margin-top: 6px; font-size: 11px; color: rgba(180,255,180,0.9);">${p.playstyle || ""}</div>
          <div style="margin-top: 8px; font-size: 11px; color: rgba(200,255,255,0.85);">
            SPD ${p.stats?.speed ?? "?"} • HP ${p.stats?.health ?? "?"} • DMG ${p.stats?.damage ?? "?"}
          </div>
        `;
        btn.addEventListener("click", () => {
          activeIndex = idx;
          renderLeft();
          renderRight();
        });
        left.appendChild(btn);
      });
    };

    const renderRight = () => {
      const p = list[activeIndex] || list[0];
      if (!p) return;

      right.innerHTML = "";

      const headline = document.createElement("div");
      headline.style.cssText = `display:flex; gap: 16px; align-items: flex-start;`;

      const headlineText = document.createElement("div");
      headlineText.style.cssText = `flex: 1 1 auto; min-width: 0;`;

      const titleRow = document.createElement("div");
      titleRow.style.cssText = `display:flex;justify-content:space-between;align-items:baseline;gap:10px;`;
      titleRow.innerHTML = `
        <div style="font-size: 24px; font-weight: bold; letter-spacing: 3px;">▌ ${p.name || String(p.id || "").toUpperCase()}</div>
        <div style="font-size: 11px; color: rgba(160,255,255,0.7);">${p.ship || ""}</div>
      `;

      headlineText.appendChild(titleRow);

      const subRow = document.createElement("div");
      subRow.style.cssText = `margin-top: 8px; color: rgba(200,255,255,0.85); font-size: 12px; line-height: 1.45;`;
      subRow.textContent = p.bio || "";

      const quoteRow = document.createElement("div");
      quoteRow.style.cssText =
        "margin-top: 10px; padding: 10px 12px; border-left: 2px solid rgba(0,255,255,0.6); background: rgba(0,0,0,0.18); color: rgba(180,255,180,0.95); font-style: italic; white-space: pre-line;";
      quoteRow.textContent = p.quote || '"No recorded quotes."';

      const stats = document.createElement("div");
      stats.style.cssText = `margin-top: 14px; display:grid; grid-template-columns: repeat(${isNarrow ? '2' : '3'}, 1fr); gap: 10px;`;
      const statBox = (label, value, color) => {
        const box = document.createElement("div");
        box.style.cssText =
          "padding: 10px 12px; border: 1px solid rgba(0,255,255,0.25); background: rgba(0,0,0,0.14);";
        box.innerHTML = `
          <div style="font-size: 10px; color: rgba(160,255,255,0.75); letter-spacing: 2px;">${label}</div>
          <div style="margin-top: 4px; font-size: 18px; color: ${color}; font-weight: bold;">${value}</div>
        `;
        return box;
      };
      stats.appendChild(statBox("SPEED", p.stats?.speed ?? "?", "#00ffff"));
      stats.appendChild(statBox("HEALTH", p.stats?.health ?? "?", "#00ff00"));
      stats.appendChild(statBox("DAMAGE", p.stats?.damage ?? "?", "#ff6600"));

      const special = document.createElement("div");
      special.style.cssText =
        "margin-top: 12px; padding: 10px 12px; border: 1px solid rgba(255, 102, 0, 0.35); background: rgba(255, 102, 0, 0.06); color: rgba(255, 200, 160, 0.95);";
      special.innerHTML = `<div style="font-size:10px;letter-spacing:2px;color:rgba(255,200,160,0.85);">SPECIAL</div><div style="margin-top:4px;font-size:12px;">${p.special || ""}</div>`;

      const matrix = document.createElement("div");
      matrix.style.cssText = `margin-top: 16px;`;
      const pm = p.personality || {};
      matrix.appendChild(personalityBar("AGGRESSION", pm.aggression, "#ff6600"));
      matrix.appendChild(personalityBar("EQUITY", pm.equity, "#00ff00"));
      matrix.appendChild(personalityBar("STABILITY", pm.stability, "#00ffff"));
      matrix.appendChild(personalityBar("CHAOS", pm.chaos, "#ff00ff"));

      if (p.archetype || p.species) {
        const meta = document.createElement("div");
        meta.style.cssText = "margin-top: 6px; color: rgba(160,255,255,0.7); font-size: 11px;";
        meta.textContent = [p.species, p.archetype].filter(Boolean).join(" • ");
        headlineText.appendChild(meta);
      }

      const playstyle = document.createElement("div");
      playstyle.style.cssText = "margin-top: 8px; color: rgba(180,255,180,0.95); font-size: 11px;";
      playstyle.textContent = p.playstyle || "";
      headlineText.appendChild(playstyle);

      headline.appendChild(portraitBox);
      headline.appendChild(headlineText);
      right.appendChild(headline);
      right.appendChild(subRow);
      right.appendChild(quoteRow);
      right.appendChild(stats);
      right.appendChild(special);
      right.appendChild(matrix);

      // Always show cycling portraits immediately; 3D full-profile preview loads in the background.
      startPortraitCycleFor(p);

      // Let the DOM paint first so switching profiles never feels janky.
      scheduledGlbStartToken += 1;
      const token = scheduledGlbStartToken;
      setTimeout(() => {
        if (!document.getElementById("character-select-overlay")) return;
        // If the user moved selection again, skip this stale start.
        if (token !== scheduledGlbStartToken) return;
        startGlbPreviewFor(p).catch(() => {});
      }, 260);
    };

    overlay.appendChild(frame);
    frame.appendChild(header);
    frame.appendChild(body);
    body.appendChild(left);
    body.appendChild(right);
    frame.appendChild(footer);
    document.body.appendChild(overlay);

    const resolveWithActive = (resolve) => {
      const p = list[activeIndex] || list[0];
      const chosen = p?.id || null;
      window.removeEventListener("keydown", onKeyDown, true);
      stopPortraitCycle();
      stopGlbPreview();
      overlay.remove();
      resolve(chosen);
    };

    const onKeyDown = (e) => {
      const code = e.code;
      const isNav =
        code === "ArrowUp" ||
        code === "ArrowDown" ||
        code === "KeyW" ||
        code === "KeyS" ||
        code === "Enter" ||
        code === "Space" ||
        code === "Escape";
      if (!isNav) return;

      // Prevent Game key handlers from observing these during selection.
      e.preventDefault();
      e.stopPropagation();
      e.stopImmediatePropagation?.();

      if (code === "ArrowUp" || code === "KeyW") {
        activeIndex = (activeIndex - 1 + list.length) % list.length;
        renderLeft();
        renderRight();
      } else if (code === "ArrowDown" || code === "KeyS") {
        activeIndex = (activeIndex + 1) % list.length;
        renderLeft();
        renderRight();
      }
      // Deploy on Enter/Space/Escape (escape deploys current so there’s no dead-end).
      if (code === "Enter" || code === "Space" || code === "Escape") {
        deployBtn.click();
      }
    };

    renderLeft();
    renderRight();

    return new Promise((resolve) => {
      deployBtn.addEventListener("click", () => resolveWithActive(resolve));
      randomBtn.addEventListener("click", () => {
        activeIndex = Math.floor(Math.random() * list.length);
        renderLeft();
        renderRight();
      });
      window.addEventListener("keydown", onKeyDown, true);
    });
  }
  
  /**
   * Cleanup
   */
  dispose() {
    this._hideLowHealthOverlay();
    if (this.animFrame) {
      cancelAnimationFrame(this.animFrame);
    }
    if (this._specialBriefingTimer) {
      clearTimeout(this._specialBriefingTimer);
      this._specialBriefingTimer = null;
    }
    
    this.uiLayer.innerHTML = '';
    
    // Clean up global listeners
    if (this.responsiveListener) {
      window.removeEventListener('resize', this.responsiveListener);
    }
    
    this.elements = {};
  }

  setHUDVisible(isVisible) {
    const hudMain = document.getElementById('hud-main');
    if (!hudMain) return;
    const defaultDisplay = hudMain.dataset.smDefaultDisplay || 'grid';
    hudMain.style.display = isVisible ? defaultDisplay : 'none';

    const reticle = document.getElementById("hud-reticle");
    if (reticle) reticle.style.display = isVisible ? "" : "none";
  }

  toggleHUD() {
    const hudMain = document.getElementById('hud-main');
    if (!hudMain) return;
    const isHidden = hudMain.style.display === 'none';
    this.setHUDVisible(isHidden);
  }
}
